import axios from 'axios';
import { User, Simulation, LoginData, RegisterData } from '../types';
import type { PilotAssignment } from '../lib/pilotAnalytics';
import type {
  ModelLabCatalog,
  ModelLabSelection,
  ModelLabSessionRow,
  ModelLabTestPurpose,
  ModelLabTestResult,
} from '../lib/modelLab';
import type { PilotConfig, PilotConfigResponse, PilotProgressPayload } from '../types/pilot';
import type { FocusArea, FocusSource, RecommendedFocus } from '../types/onboarding';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

let accessToken: string | null = null;
let csrfToken: string | null = null;
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift() || null;
  return null;
};

export const getAccessToken = () => accessToken;

export const setTokens = (token?: string | null, csrf?: string | null) => {
  const previous = accessToken;
  accessToken = token || null;
  csrfToken = csrf || null;
  /* Drop cached GETs whenever the session IDENTITY changes — not only on
   * logout.
   *
   * The logout-only guard had a hole: signing in as a different user WITHOUT
   * logging out first (session expired, or straight to /login) sets a new
   * truthy token, so the old user's cached responses survived in this
   * module-level Map for up to their TTL — 5 minutes for /simulations. The
   * new user then rendered the previous user's payload.
   *
   * Caught 19 Aug: a manager reviewing a submission saw a "Start" button on
   * a template that is not practisable for them, because the card carried the
   * AUTHOR's `canPractise: true` from the author's cached payload. Benign
   * there — both could see that scenario — but the same cache could just as
   * easily hold rows the new user has no right to.
   *
   * A same-user token refresh also lands here and clears; that costs one
   * refetch and keeps the rule simple ("new token, new cache"), which is the
   * right trade for a correctness guard. */
  if (!token || previous !== token) {
    getCache.clear();
    inflightGets.clear();
  }
};

const getCsrfHeader = () => {
  const csrf = csrfToken || getCookie('csrf');
  return csrf ? { 'X-CSRF-Token': csrf } : {};
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with CORS
});

// --- Lightweight GET caching + request de-duping (reduces network + smooths intermittent latency) ---
type CacheEntry = { at: number; data: any };
const GET_CACHE_TTL_DEFAULT_MS = 12_000;
// The /simulations catalogue is heavy and re-fetched on every Practice mount
// (Simulations mounts even on the Recordings tab). Scenario mutations already
// invalidateCachedGet('/simulations?'), so a longer window is safe and keeps
// returning to /practice instant instead of refetching.
const SIMULATIONS_CACHE_TTL_MS = 5 * 60_000;
const getCache = new Map<string, CacheEntry>();
const inflightGets = new Map<string, Promise<any>>();

// Per-key generation counters. Bumped on invalidation so an in-flight
// fetch that started BEFORE a mutation can't repopulate the cache with
// pre-mutation data when it lands AFTER the invalidation. (This race
// predates the SWR mode below but matters much more with background
// revalidation constantly in flight.)
const keyGen = new Map<string, number>();

function invalidateCachedGet(prefix: string) {
  try {
    const touched = new Set<string>();
    for (const k of Array.from(getCache.keys())) {
      if (k.startsWith(prefix)) {
        getCache.delete(k);
        touched.add(k);
      }
    }
    for (const k of Array.from(inflightGets.keys())) {
      if (k.startsWith(prefix)) {
        inflightGets.delete(k);
        touched.add(k);
      }
    }
    touched.forEach((k) => {
      keyGen.set(k, (keyGen.get(k) || 0) + 1);
    });
  } catch {}
}

function stableStringify(obj: any) {
  try {
    if (!obj) return '';
    const keys = Object.keys(obj).sort();
    const out: any = {};
    for (const k of keys) out[k] = obj[k];
    return JSON.stringify(out);
  } catch {
    return String(obj || '');
  }
}

/**
 * GET with in-memory caching + in-flight dedupe.
 *
 * Modes:
 *   default          — fresh entry returned; expired entry refetched
 *                      (caller waits for the network).
 *   swr: true        — stale-while-revalidate. An EXPIRED entry is
 *                      returned instantly while a background refetch
 *                      updates the cache for the next read. Callers
 *                      paint immediately on navigation; freshness
 *                      arrives one read later. Bounded by
 *                      `swrMaxAgeMs` (default 10 min): entries older
 *                      than that are treated as cold — serving a
 *                      2-hour-old dashboard "instantly" helps nobody.
 *
 * Both modes guard cache writes with the per-key generation counter:
 * a fetch that started before invalidateCachedGet() ran does NOT
 * write its (pre-mutation) result into the cache.
 */
const SWR_MAX_AGE_DEFAULT_MS = 10 * 60_000;

async function cachedGet<T = any>(
  url: string,
  params?: any,
  ttlMs: number = GET_CACHE_TTL_DEFAULT_MS,
  opts?: { swr?: boolean; swrMaxAgeMs?: number },
): Promise<T> {
  const key = `${url}?${stableStringify(params || {})}`;
  const cached = getCache.get(key);
  const ageMs = cached ? Date.now() - cached.at : Infinity;
  if (cached && ageMs < ttlMs) return cached.data as T;

  const startFetch = (): Promise<T> => {
    const existing = inflightGets.get(key);
    if (existing) return existing as Promise<T>;
    const gen = keyGen.get(key) || 0;
    const p = api
      .get(url, params ? { params } : undefined)
      .then((resp) => {
        // Generation check — drop the write if a mutation invalidated
        // this key while we were in flight.
        if ((keyGen.get(key) || 0) === gen) {
          getCache.set(key, { at: Date.now(), data: resp.data });
        }
        return resp.data as T;
      })
      .catch((e) => {
        if ((keyGen.get(key) || 0) === gen) getCache.delete(key);
        throw e;
      })
      .finally(() => {
        if (inflightGets.get(key) === p) inflightGets.delete(key);
      });
    inflightGets.set(key, p);
    return p;
  };

  // SWR: expired-but-not-ancient entry → serve it now, revalidate in
  // the background (errors swallowed — the stale data already served
  // the caller; the next read retries).
  const maxStale = opts?.swrMaxAgeMs ?? SWR_MAX_AGE_DEFAULT_MS;
  if (opts?.swr && cached && ageMs < maxStale) {
    startFetch().catch(() => {
      /* background revalidation is best-effort */
    });
    return cached.data as T;
  }

  return startFetch();
}

// Attach access token
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

const tryRefresh = async () => {
  const headers = getCsrfHeader();
  if (!headers['X-CSRF-Token']) throw new Error('Missing CSRF token');
  const resp = await refreshClient.post('/auth/refresh', {}, { headers });
  const data = resp.data || {};
  setTokens(data.accessToken, data.csrfToken);
  return data;
};

/**
 * Single-flight session refresh. EVERY caller must go through this — the 401
 * interceptor below and the app-boot refresh in AuthContext alike.
 *
 * Concurrent refreshes are not safe server-side. Each one rotates the refresh
 * token and mints a fresh csrf, so two running in parallel both return 200 but
 * hand back *different* csrf tokens: the browser keeps whichever Set-Cookie
 * landed last, while the server may have recorded the other jti as the active
 * session. The next refresh then fails the concurrent-session check and 401s,
 * logging the user out at random — roughly every other page load, because boot
 * refresh and interceptor refresh raced each other.
 *
 * Deliberately does NOT clear tokens or emit auth:session-expired on failure.
 * Boot-time refresh legitimately fails for signed-out visitors, and firing a
 * "session expired" toast at them would be wrong. Callers decide: the
 * interceptor treats a rejection as an expiry, AuthContext treats it as
 * "not logged in".
 */
export const refreshSession = (): Promise<any> => {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;
  refreshPromise = tryRefresh().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });
  return refreshPromise;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const reqUrl = error.config?.url || '';
    const isAuthEndpoint =
      reqUrl.includes('/auth/login') ||
      reqUrl.includes('/auth/register') ||
      reqUrl.includes('/auth/refresh');

    if (status === 401 && !isAuthEndpoint) {
      const reason = error.response?.data?.reason;

      // Session timeout (inactivity) or session replaced (concurrent login) — don't try refresh
      if (reason === 'session_timeout' || reason === 'session_replaced') {
        setTokens(null, null);
        const message =
          reason === 'session_timeout'
            ? 'Your session has expired due to inactivity.'
            : 'You have been logged out (logged in from another device).';
        window.dispatchEvent(
          new CustomEvent('auth:session-expired', {
            detail: { message },
          }),
        );
      } else {
        // Token expired — refresh via the shared single-flight helper so this
        // never races the app-boot refresh, and so every 401 that lands while
        // a refresh is already running waits on that same promise.
        //
        // This used to be `else if (!isRefreshing)`, which meant a concurrent
        // 401 matched no branch at all: it was never queued, retried or
        // rejected, so its promise never settled and the caller hung forever.
        try {
          await refreshSession();
        } catch {
          // Refresh genuinely failed — this really is an expired session.
          setTokens(null, null);
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        // On success retry with the new token. On failure accessToken is null
        // and we fall through to the shared reject below — never left pending.
        if (accessToken) {
          const cfg = { ...error.config };
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${accessToken}`;
          return api(cfg);
        }
      }
    }

    // 402 Payment Required — workspace-trial gating fires. Two distinct
    // reason codes from backend/middleware/auth.js drive two distinct
    // frontend reactions:
    //   - 'trial_expired'                 → owner whose trial ran out
    //                                       (route to /subscription)
    //   - 'owner_subscription_required'   → teammate of an expired-trial
    //                                       workspace (render block screen)
    // Each fires its own global event; the App-level listeners decide the
    // navigation. Rejecting the promise still lets individual callers
    // handle the error if they want a custom flow.
    if (status === 402) {
      try {
        const reason = error.response?.data?.reason;
        const here =
          typeof window !== 'undefined' && window.location
            ? window.location.pathname + window.location.search
            : '';

        // Don't yank the user mid-flow while they're on the subscription page
        // or actively in the self-serve signup/checkout wizard (/get-started)
        // — both already lead to payment. A not-yet-subscribed new owner (e.g.
        // self-serve, who gets no trial) can have background calls like data
        // prefetch 402 during the wizard; suppressing the redirect there keeps
        // the wizard intact until they reach Stripe checkout.
        if (
          reason === 'trial_expired' &&
          !here.startsWith('/subscription') &&
          !here.startsWith('/get-started')
        ) {
          const upgradeUrl = error.response?.data?.upgradeUrl || '/subscription';
          window.dispatchEvent(
            new CustomEvent('auth:trial-expired', {
              detail: { upgradeUrl, attemptedPath: here },
            }),
          );
        } else if (reason === 'owner_subscription_required') {
          // No navigation — the block screen renders in-place via the App
          // listener. Pass the owner email through so the screen can
          // address them by name.
          const ownerEmail = error.response?.data?.ownerEmail || null;
          window.dispatchEvent(
            new CustomEvent('auth:owner-subscription-required', {
              detail: { ownerEmail, attemptedPath: here },
            }),
          );
        }
      } catch (_) {}
    }

    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  // Public: who a personal email link was for. Not a credential, it only
  // fills the login form (backend utils/emailLinks.js). Throws (404) when bad.
  resolveEmailLink: async (token: string) => {
    const response = await api.get('/auth/email-link', { params: { t: token } });
    return response.data as { valid: boolean; userId: string; email: string; path: string };
  },
  /** Tell the server the user is still present. See POST /auth/heartbeat:
   *  a live simulation makes no other authenticated call, so without this the
   *  inactivity timer runs down mid-conversation. Resolves either way — a
   *  failed ping must never surface to a learner who is mid-sentence. */
  heartbeat: async () => {
    try {
      await api.post('/auth/heartbeat');
      return true;
    } catch {
      return false;
    }
  },

  login: async (data: LoginData) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  /** Ask for a one-time sign-in code by email.
   *  Always resolves with the same generic message whether or not the address
   *  has an account - deliberately, so the response cannot be used to discover
   *  who is registered. A 503 means the feature is switched off (no template
   *  configured), which is the one case worth showing the user verbatim. */
  requestLoginOtp: async (email: string) => {
    const response = await api.post('/auth/otp/request', { email });
    return response.data as { message: string };
  },

  /** Exchange a code for a session. Returns the same shape as login(). */
  verifyLoginOtp: async (email: string, code: string) => {
    const response = await api.post('/auth/otp/verify', { email, code });
    return response.data;
  },

  /** Approach A success-page completion: verify the paid Stripe session,
   *  provision the owner workspace, and (on success) return auth tokens for
   *  auto-login. Returns { status: 'processing' } (HTTP 202) while the webhook
   *  is still provisioning — the caller should retry. */
  signupComplete: async (sessionId: string) => {
    const response = await api.post('/auth/signup-complete', { sessionId });
    return response.data as
      { accessToken: string; csrfToken: string; user: any } | { status: 'processing' };
  },

  register: async (data: RegisterData) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  googleAuth: async (
    credential: string,
    company?: string,
    department?: string,
    position?: string,
    extra?: {
      companyId?: string;
      /** The team half of a team invite link (/join). */
      teamId?: string;
      companyName?: string;
      isAdmin?: boolean;
      role?: 'employee' | 'manager' | 'admin';
      lanuraInvite?: string;
      mode?: 'login';
      selfServeOwner?: boolean;
    },
  ) => {
    const response = await api.post('/auth/google', {
      credential,
      company,
      department,
      position,
      ...(extra || {}),
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateProfile: async (data: Partial<User>) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (payload: { currentPassword?: string; newPassword: string }) => {
    const response = await api.post('/auth/change-password', payload);
    return response.data;
  },

  logout: async () => {
    const response = await api.post(
      '/auth/logout',
      {},
      {
        headers: getCsrfHeader(),
      },
    );
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  verifyEmail: async (token: string, email: string) => {
    const response = await api.post('/auth/verify-email', { token, email });
    return response.data;
  },

  resetPassword: async (data: any) => {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  },

  // Delegates to the shared single-flight helper. Previously this issued its
  // own POST, so the app-boot refresh in AuthContext could run concurrently
  // with an interceptor refresh — the exact race that rotated the session
  // twice and logged users out on roughly every other page load.
  refresh: async () => refreshSession(),

  /** Generate an owner invite link. Backend is gated to the pinned
   *  superAdmin identity (owner role + specific email); non-matching
   *  callers get a 403. The backend proxies to the email service's
   *  /generate-invite endpoint, returning { inviteLink }. The email
   *  service URL is never exposed to the browser. */
  generateOwnerInvite: async () => {
    const response = await api.post('/auth/invite/generate-owner');
    return response.data as { inviteLink: string };
  },

  /* ── superAdmin: the dev-subdomain portal-access queue ──
   * Unverified accounts cannot get in by themselves; the superAdmin opens a
   * six-hour window. All three are superAdmin-pinned server-side — the UI
   * gating is cosmetic, these 403 for anyone else. */

  listAccessRequests: async () => {
    const response = await api.get('/auth/admin/access-requests');
    return response.data as AccessRequestsResponse;
  },

  grantPortalAccess: async (
    userId: string,
    duration?: string,
    customMinutes?: number,
    kind?: string,
  ) => {
    // A KEY from the server's menu, or a hand-entered minute count the server
    // clamps to its own bounds. Never a raw duration. See GRANT_DURATIONS.
    const response = await api.post(
      `/auth/admin/access-requests/${encodeURIComponent(userId)}/grant`,
      { duration, customMinutes, kind },
    );
    return response.data as { ok: true; request: AccessRequest };
  },

  denyPortalAccess: async (userId: string, reason?: string, kind?: string) => {
    const response = await api.post(
      `/auth/admin/access-requests/${encodeURIComponent(userId)}/deny`,
      { reason, kind },
    );
    return response.data as { ok: true; request: AccessRequest };
  },
};

/** One account in the portal-access queue. `blocked`/`reason` are computed by
 *  the same code as the login gate, so the page cannot disagree with it. */
export interface AccessRequest {
  /** Which population this row belongs to. An app user holds a practice seat;
   *  a developer account holds API keys and a credit balance. Granting them
   *  are different decisions, so the page never blurs the two. */
  kind: 'app' | 'developer';
  userId: string;
  name: string | null;
  email: string | null;
  company: string | null;
  role: string | null;
  /** Developer rows only — what granting actually unlocks. */
  credits: number | null;
  liveEnabled: boolean;
  createdAt: string | null;
  lastActive: string | null;
  emailVerified: boolean;
  status: 'none' | 'pending' | 'granted' | 'denied';
  requestedAt: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  expiresAt: string | null;
  denyReason: string | null;
  requestCount: number;
  /** Which option from the menu this grant was given for, e.g. '24h'. */
  durationKey: string | null;
  /** Asked within the console's freshness window. False for accounts that have
   *  never asked — they are blocked, but nobody is waiting on them. */
  requestFresh: boolean;
  blocked: boolean;
  reason:
    | 'verified'
    | 'granted'
    | 'verification_required'
    | 'awaiting_approval'
    | 'access_expired'
    | 'access_denied';
  /** Time left on an active grant. null when there is no readable expiry. */
  msRemaining: number | null;
}

export interface AccessRequestsResponse {
  requests: AccessRequest[];
  /** True when the scan hit its cap — the list is not the whole picture. */
  truncated: boolean;
  policy: {
    approvalEnabled: boolean;
    strict: boolean;
    grantDurationMs: number;
    freshWindowMs: number;
    /** The durations the server will accept. Rendered as-is by the console. */
    grantDurations: Array<{ key: string; ms: number; label: string }>;
    defaultGrantKey: string;
    customMinMinutes: number;
    customMaxMinutes: number;
  };
}

// Simulations API
export const simulationsAPI = {
  getSimulations: async (filters?: any) => {
    return await cachedGet('/simulations', filters || {}, SIMULATIONS_CACHE_TTL_MS, { swr: true });
  },

  getSimulation: async (id: string) => {
    const response = await api.get(`/simulations/${id}`);
    return response.data;
  },

  getRichContext: async (simulationId: string) => {
    const response = await api.get(`/simulations/${simulationId}/rich-context`);
    return response.data as {
      richContext: {
        // V2 five-field format
        person?: { name: string; role: string; relationship: string; behaviourNote: string };
        situation?: string;
        whatTheyWant?: string;
        whatYouNeed?: string;
        challenge?: string;
        approachHints?: string[];
        culturalContext?: string;
        // Fact sheet — the role + canonical operational facts the user holds
        // going in (shared by the brief UI and the counterpart prompt).
        factSheetVersion?: number;
        yourRole?: string;
        whatYouKnow?: Array<{ label: string; text: string }>;
        // Legacy format (backward compat)
        background?: string;
        keyDetails?: Array<{ label: string; text: string }>;
        concern?: string;
      } | null;
      cached: boolean;
      legacy?: boolean;
    };
  },

  startSimulation: async (
    id: string,
    meta?: {
      pathwaySituationId?: string | null;
      source?: string;
      modality?: 'video' | 'voice' | 'text';
      forceNew?: boolean;
    },
  ) => {
    const response = await api.post(`/simulations/${id}/start`, meta || {});
    return response.data;
  },

  submitResponse: async (sessionId: string, message: string) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/respond`, { message });
    return response.data;
  },

  /** Text-only simulation chat (OpenAI). messages: [{ role: 'user'|'assistant', content }]. Returns { message } (assistant reply). */
  simulationChat: async (
    sessionId: string,
    systemPrompt: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/chat`, {
      systemPrompt,
      messages,
    });
    return response.data;
  },

  /** Voice-only simulation TTS (ElevenLabs). Returns an audio Blob (mp3). */
  simulationTts: async (sessionId: string, text: string, personaKey?: string | null) => {
    const response = await api.post(
      `/simulations/sessions/${sessionId}/tts`,
      { text, personaKey: personaKey || null },
      { responseType: 'blob' as any },
    );
    return response.data as Blob;
  },

  /** Create a short-lived ticket for server-side realtime STT websocket. */
  createSttTicket: async (sessionId: string) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/stt-ticket`, {});
    return response.data as { ticket: string };
  },

  /** Get a signed URL for ElevenLabs Conversational AI WebSocket (server-minted). */
  getConvaiSignedUrl: async (sessionId: string) => {
    const response = await api.get(`/simulations/sessions/${sessionId}/convai-signed-url`);
    return response.data as {
      signedUrl?: string | null;
      agentId?: string;
      isPublic?: boolean;
      /** Model lab: the superadmin's chosen voice-agent brain, else null. */
      modelLab?: { llm: string | null } | null;
    };
  },

  /**
   * Save the conversation so far, mid-session.
   *
   * Called when the page is about to go away — pagehide, tab hide, route
   * unmount — so a session the learner never "ends" still carries its turns.
   * Until this existed, the only route a transcript had to the backend was the
   * End button; closing the tab lost the whole conversation, and an end that
   * fired from a stale closure (the idle timer) sent only the turns that were
   * on screen when the session STARTED.
   *
   * Deliberately not `api.post`: an in-flight XHR is cancelled when the
   * document unloads. `keepalive` lets the request outlive the page, and
   * unlike sendBeacon it can carry the Authorization and CSRF headers this API
   * requires. Its cost is a ~64KB body cap, hence the trim below — a ten-minute
   * conversation is a few KB, so this only ever bites on something pathological.
   */
  saveSessionTranscript: (sessionId: string, turns: any[]) => {
    if (!sessionId || !Array.isArray(turns) || turns.length === 0) return;
    const KEEPALIVE_BUDGET_BYTES = 56_000;
    let payload = turns;
    let body = JSON.stringify({ turns: payload });
    // Drop from the oldest end until it fits; the backend merge never shrinks
    // what it already holds, so earlier turns survive in previous flushes.
    while (body.length > KEEPALIVE_BUDGET_BYTES && payload.length > 1) {
      payload = payload.slice(Math.ceil(payload.length / 10));
      body = JSON.stringify({ turns: payload });
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    Object.assign(headers, getCsrfHeader());
    try {
      return fetch(`${API_BASE_URL}/simulations/sessions/${sessionId}/transcript`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers,
        body,
      }).catch(() => undefined);
    } catch {
      // Unload-time failures are expected and unactionable — the completion
      // POST, when one happens, still carries the transcript.
      return undefined;
    }
  },

  completeSimulation: async (
    sessionId: string,
    scores: any,
    feedback: any,
    durationSeconds?: number,
    slides?: number,
    metrics?: any,
    transcript?: any,
    scenario?: string,
    modality?: 'video' | 'voice' | 'text',
  ) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/complete`, {
      scores,
      feedback,
      durationSeconds,
      slides,
      metrics,
      transcript,
      scenario,
      modality,
    });
    // Completion records the session's minutes against the user's
    // seat server-side — bust the cached board/own-state so the Seats
    // UI reflects the new usage immediately instead of lagging a TTL.
    invalidateCachedGet('/seats');
    // A completed session changes nearly every progression surface
    // (pathways recompute fires server-side; dashboard strength map,
    // recordings summary and history all gain a session). Without these
    // busts, Progress/Pathways/Dashboard kept serving the pre-session
    // cache and practice visibly changed nothing.
    invalidateCachedGet('/dashboard/data');
    invalidateCachedGet('/history/recordings-summary');
    invalidateCachedGet('/history/streak-summary');
    invalidateCachedGet('/history/practice-totals');
    try {
      const { queryClient } = await import('../lib/queryClient');
      const { QK } = await import('../hooks/queries');
      const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: QK.pathwaysMap });
        queryClient.invalidateQueries({ queryKey: QK.dashboard });
        queryClient.invalidateQueries({ queryKey: ['history'] });
      };
      invalidateAll();
      // Pathways recompute is fire-and-forget server-side — refetch again
      // after a short grace so the recomputed bands/fits land, not the
      // pre-recompute snapshot.
      setTimeout(invalidateAll, 4000);
    } catch {
      /* react-query not mounted — cachedGet busts above still apply */
    }
    return response.data;
  },

  getMySessions: async () => {
    const response = await api.get('/simulations/sessions/my');
    return response.data;
  },
  addSnippet: async (
    sessionId: string,
    payload: { atMs?: number; durationMs?: number; url?: string; text?: string; tags?: string[] },
  ) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/snippets`, payload);
    return response.data;
  },
  addHighlight: async (
    sessionId: string,
    payload: { title: string; summary: string; atMs?: number; tags?: string[] },
  ) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/highlights`, payload);
    return response.data;
  },
  addTags: async (sessionId: string, tags: string[]) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/tags`, { tags });
    return response.data;
  },
  getMyHighlights: async (limit: number = 10, nextToken?: string, page: number = 1) => {
    const response = await api.get('/simulations/sessions/my-highlights', {
      params: { limit, nextToken, page },
    });
    return response.data;
  },

  generateScenario: async (data: {
    whatHappening: string;
    whoInvolved: string;
    whatHopeFor: string;
    category: string;
    difficulty: string;
    seniority: string;
    tags?: string[];
    personaKey?: string;
  }) => {
    const response = await api.post('/simulations/generate', data);
    return response.data as {
      message: string;
      content: {
        title: string;
        description: string;
        openingMessage: string;
        context: string;
        objectives: string[];
        likelyPushback?: string | null;
        skillsAssessed?: string[];
      };
      personaKey: string | null;
      personaCharacterName: string | null;
      personaGender: string | null;
    };
  },

  /** Guided mode: infer category/difficulty/seniority + plain-English tags
   *  from the step-1 answers so the user never has to classify their scenario. */
  classifyScenario: async (data: {
    whatHappening: string;
    whoInvolved: string;
    whatHopeFor?: string;
  }) => {
    const response = await api.post('/simulations/classify', data);
    return response.data as {
      category: string;
      difficulty: string;
      seniority: string;
      tags: string[];
      skillFocus: string | null;
    };
  },

  getCreateScenarioInspiration: async () => {
    const response = await api.get('/simulations/inspiration');
    return response.data;
  },

  /** Quick create: one call that parses a plain-English description into the
   *  guided wizard's structured fields AND generates the full scenario content. */
  /**
   * Ask Lanura to rewrite a scenario so specific criteria have a genuine reason
   * to appear. Returns a PROPOSAL — nothing is saved, because this rewrites the
   * author's situation and they only asked what it would look like.
   */
  adjustForCriteria: async (simulationId: string, rubricId: string, criteria: string[]) => {
    const r = await api.post(`/simulations/${simulationId}/adjust-for-criteria`, {
      rubricId,
      criteria,
    });
    return r.data as {
      proposal: {
        context: string;
        openingMessage: string;
        objectives: string[];
        changes: string[];
      };
      criteria: Array<{ key: string; label: string; state: string }>;
      categoryHint: null | { category: string; wouldCover: string[] };
      openingKept: boolean;
    };
  },

  /** Mint a short-lived OpenAI Realtime token for LIVE dictation — the
   *  browser opens its own websocket with this single-purpose secret and
   *  streams mic audio; the real API key never leaves the server. */
  dictationToken: async () => {
    const response = await api.post('/simulations/dictation-token');
    return response.data as { token: string; expiresAt: number | null };
  },

  /** Speak the quick-create brief instead of typing it. Sends the raw audio
   *  clip; the server transcribes it (same model as session STT recovery) and
   *  returns plain text for the description box. Nothing is stored. */
  transcribeBrief: async (audio: Blob) => {
    const response = await api.post('/simulations/transcribe-brief', audio, {
      headers: { 'Content-Type': audio.type || 'audio/webm' },
      timeout: 85000, // just under the backend's 90s heavy-route watchdog
    });
    return response.data as { text: string };
  },

  quickGenerateScenario: async (
    description: string,
    personaKey?: string,
    /** Generate FOR a framework: the scenario is written to create the moments
     *  those criteria need, and filed under the category whose baselines
     *  actually measure them. Omit and this is the create flow unchanged. */
    forCriteria?: { rubricId: string; criteria?: string[] },
  ) => {
    const response = await api.post('/simulations/quick-generate', {
      description,
      ...(personaKey ? { personaKey } : {}),
      ...(forCriteria?.rubricId
        ? { rubricId: forCriteria.rubricId, criteria: forCriteria.criteria || [] }
        : {}),
    });
    return response.data as {
      message: string;
      content: {
        title: string;
        description: string;
        openingMessage: string;
        context: string;
        objectives: string[];
        likelyPushback?: string | null;
        skillsAssessed?: string[];
      };
      structured: {
        whoInvolved: string;
        whatHappening: string;
        whatHopeFor: string;
        category: string;
        difficulty: string;
        seniority: string;
        tags: string[];
        skillFocus?: string | null;
      };
      personaKey: string | null;
      personaCharacterName: string | null;
      personaGender: string | null;
    };
  },

  /** Skill-gap-driven starting points for Quick create (server caches 24h). */
  getIdeaStarters: async () => {
    return (await cachedGet('/simulations/idea-starters', {}, 30 * 60_000)) as {
      ideas: Array<{ text: string; skills?: string[]; category?: string }>;
      generatedAt: string | null;
      personalised: boolean;
    };
  },

  refreshCreateScenarioInspiration: async () => {
    const response = await api.post('/simulations/inspiration/refresh', {});
    return response.data;
  },

  createSimulation: async (data: Partial<Simulation>) => {
    const response = await api.post('/simulations/create', data);
    // Ensure the next catalogue load reflects the newly created scenario immediately.
    invalidateCachedGet('/simulations?');
    return response.data;
  },

  submitUserFeedback: async (
    sessionId: string,
    feedback: { rating: number; issues?: string[]; comment?: string },
  ) => {
    const response = await api.post(`/simulations/sessions/${sessionId}/feedback`, feedback);
    return response.data;
  },

  deleteSimulation: async (simulationId: string) => {
    const response = await api.delete(`/simulations/${simulationId}`);
    // Ensure the next catalogue load reflects deletions immediately.
    invalidateCachedGet('/simulations?');
    return response.data;
  },

  /**
   * Remix: copy a scenario you can read into your own private, editable one.
   * Returns the NEW scenario, not the source. The copy is always private —
   * remixing a company-wide scenario must not publish yours — so the caller
   * should send the user to their own scenarios, not back to the source.
   */
  remixSimulation: async (simulationId: string, title?: string) => {
    const response = await api.post(`/simulations/${simulationId}/remix`, title ? { title } : {});
    // The copy belongs in the viewer's catalogue on the next load.
    invalidateCachedGet('/simulations?');
    return response.data;
  },

  // ── Scenario sharing / reshare / block / approve (v2) ─────────────────
  // All five return the fresh scenario record so the caller can update its
  // local list state without a full refetch. The /share endpoint may return
  // 400 with `managedTeamIds` when the owner is a manager managing >1 teams
  // and didn't pass a `teamId` — callers must handle that case explicitly.

  /** The sub-dimensions a scenario assesses: they pick the learner's
   *  reflection questions. AI by default, changeable by the author and by
   *  company admins and owners. Never scoring. */
  getSubDimensions: async (simulationId: string) => {
    const response = await api.get(`/simulations/${simulationId}/sub-dimensions`);
    return response.data as {
      assessed: { keys: string[]; source: 'ai' | 'manual' | 'default' };
      options: Array<{ key: string; label: string; domain: string | null; definition: string }>;
      min: number;
      max: number;
      canEdit: boolean;
    };
  },
  setSubDimensions: async (simulationId: string, body: { keys?: string[]; reset?: boolean }) => {
    const response = await api.put(`/simulations/${simulationId}/sub-dimensions`, body);
    return response.data as {
      assessed: { keys: string[]; source: 'ai' | 'manual' | 'default' };
    };
  },
  shareScenario: async (simulationId: string, enabled: boolean, teamId?: string) => {
    const response = await api.post(`/simulations/${simulationId}/share`, {
      enabled,
      ...(teamId ? { teamId } : {}),
    });
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  /** Edit a template you own, while it is still unpublished. 403 with
   *  reason:'published' once it has reached a library — Remix from there. */
  editSimulation: async (simulationId: string, patch: Record<string, any>) => {
    const response = await api.patch(`/simulations/${simulationId}`, patch);
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation; withdrawn?: boolean };
  },

  /** Reviewer says no. Optional note; the author is notified either way. */
  declineSubmission: async (simulationId: string, note?: string) => {
    const response = await api.post(`/simulations/${simulationId}/decline`, note ? { note } : {});
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  /** Reviewer asks for revisions. Note is REQUIRED — a bare "changes
   *  requested" tells the author nothing actionable. */
  requestSubmissionChanges: async (simulationId: string, note: string) => {
    const response = await api.post(`/simulations/${simulationId}/request-changes`, { note });
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  reshareScenario: async (simulationId: string, teamId?: string) => {
    const response = await api.post(`/simulations/${simulationId}/reshare`, {
      ...(teamId ? { teamId } : {}),
    });
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  companyReshareScenario: async (simulationId: string) => {
    const response = await api.post(`/simulations/${simulationId}/company-reshare`, {});
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  /** How this workspace has narrowed Lanura's seeded library, plus whether the
   *  caller may change it. Readable by any member — the list they already
   *  receive reflects the same state. */
  getLibraryVisibility: async () => {
    const response = await api.get('/simulations/company-library/visibility');
    return response.data as {
      hideLibrary: boolean;
      hiddenIds: string[];
      shownIds: string[];
      canCurate: boolean;
    };
  },

  /**
   * Replace all three pieces of library visibility. FULL replacement, not a
   * delta — two curators acting at once then land on a state one of them chose
   * rather than an interleaving neither did.
   * Owner, or the paying admin, only; 403 otherwise.
   */
  setLibraryVisibility: async (next: {
    hideLibrary: boolean;
    hiddenIds: string[];
    shownIds: string[];
  }) => {
    const response = await api.put('/simulations/company-library/visibility', next);
    invalidateCachedGet('/simulations?');
    return response.data as { hideLibrary: boolean; hiddenIds: string[]; shownIds: string[] };
  },

  /* ── SuperAdmin: per-company curation of the seeded library ───────────
   * The layer ABOVE the workspace's own. A scenario hidden here never
   * reaches that company, not even its owner. SuperAdmin only, 403 otherwise. */
  getLibraryCurationOverview: async () => {
    const response = await api.get('/simulations/admin/library-curation');
    return response.data as {
      companies: {
        companyId: string;
        name: string;
        platformHidden: number;
        platformHideLibrary: boolean;
      }[];
      catalogue: {
        id: string;
        title: string;
        category: string | null;
        difficulty: string | null;
        duration: number | null;
        /** Empty = reaches every role. */
        roles: string[];
      }[];
    };
  },

  getCompanyLibraryCuration: async (companyId: string) => {
    const response = await api.get(
      `/simulations/admin/library-curation/${encodeURIComponent(companyId)}`,
    );
    return response.data as {
      companyId: string;
      name: string;
      hideLibrary: boolean;
      hiddenIds: string[];
      workspace: { hideLibrary: boolean; hiddenIds: string[]; shownIds: string[] };
    };
  },

  /** FULL replacement of the company's platform layer. */
  setCompanyLibraryCuration: async (
    companyId: string,
    next: { hideLibrary: boolean; hiddenIds: string[] },
  ) => {
    const response = await api.put(
      `/simulations/admin/library-curation/${encodeURIComponent(companyId)}`,
      next,
    );
    invalidateCachedGet('/simulations?');
    return response.data as { companyId: string; hideLibrary: boolean; hiddenIds: string[] };
  },

  /** Unpublish someone else's template. `reason` is REQUIRED by the server —
   *  a takedown with no explanation leaves the author with a scenario that
   *  silently stopped appearing. */
  blockScenarioReshare: async (simulationId: string, reason: string) => {
    const response = await api.post(`/simulations/${simulationId}/block`, { reason });
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  unblockScenarioReshare: async (simulationId: string) => {
    const response = await api.post(`/simulations/${simulationId}/unblock`, {});
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  // ── Lifecycle: force-delete / restore / recently-deleted ───────────────
  // `deleteSimulation` above is the owner self-delete (already soft-delete
  // on the backend). These three are the admin/owner takedown path and the
  // recovery surface for owners.

  forceDeleteSimulation: async (simulationId: string, reason: string) => {
    const response = await api.post(`/simulations/${simulationId}/force-delete`, { reason });
    invalidateCachedGet('/simulations?');
    return response.data;
  },

  restoreSimulation: async (simulationId: string) => {
    const response = await api.post(`/simulations/${simulationId}/restore`, {});
    invalidateCachedGet('/simulations?');
    return response.data as { scenario: Simulation };
  },

  getRecentlyDeleted: async () => {
    const response = await api.get('/simulations/recently-deleted');
    return response.data as { scenarios: Simulation[] };
  },

  // superAdmin break-glass (server enforces the pinned identity + ownerless scope).
  superadminDeleteScenario: async (simulationId: string) => {
    const response = await api.post(`/simulations/${simulationId}/superadmin-delete`, {});
    invalidateCachedGet('/simulations?');
    invalidateCachedGet('/simulations/superadmin/');
    return response.data as { message: string };
  },
  superadminRestoreScenario: async (simulationId: string) => {
    const response = await api.post(`/simulations/${simulationId}/superadmin-restore`, {});
    invalidateCachedGet('/simulations?');
    invalidateCachedGet('/simulations/superadmin/');
    return response.data as { message: string };
  },
  // Heavy (full-table scans server-side) and unchanged between superadmin
  // delete/restore — cache so tab-switching back to Legacy is instant.
  // Invalidated by the two mutations above.
  superadminListLegacy: async () => {
    return (await cachedGet(
      '/simulations/superadmin/legacy',
      {},
      SIMULATIONS_CACHE_TTL_MS,
    )) as Simulation[];
  },
  superadminListDeleted: async () => {
    return (await cachedGet(
      '/simulations/superadmin/deleted',
      {},
      SIMULATIONS_CACHE_TTL_MS,
    )) as Array<{
      simulationId: string;
      title: string;
      category: string;
      deletedAt: string | null;
    }>;
  },
};

// Notifications API
export const notificationsAPI = {
  getMine: async (opts?: { limit?: number; unreadOnly?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.unreadOnly) params.set('unread', '1');
    const qs = params.toString();
    const response = await api.get(`/notifications${qs ? `?${qs}` : ''}`);
    return response.data as {
      notifications: NotificationItem[];
      unreadCount: number;
    };
  },
  markRead: async (notifId: string, createdAt: string) => {
    await api.post(`/notifications/${notifId}/read`, { createdAt });
  },
  markAllRead: async () => {
    await api.post('/notifications/read-all', {});
  },
};

export type NotificationItem = {
  notifId: string;
  userId: string;
  type: string;
  scenarioId: string | null;
  message: string | null;
  payload: Record<string, any> | null;
  readAt: string | null;
  createdAt: string;
};

// Quiz v2 API
export const quizAPI = {
  getV2: async () => {
    const response = await api.get('/quiz/v2');
    return response.data;
  },
  startV2: async () => {
    const response = await api.post('/quiz/v2/start', {});
    return response.data;
  },
  submitV2: async (payload: { attempt_id: string; answers: any[] }) => {
    const response = await api.post('/quiz/v2/submit', payload);
    return response.data;
  },
  resetV2: async () => {
    const response = await api.post('/quiz/v2/reset', {});
    return response.data;
  },
  getMyTraits: async () => {
    const response = await api.get('/users/me/traits');
    return response.data;
  },
};

// Style API — quiz-derived archetype (server-authoritative) + what Lanura
// has learned from observed practice (behaviour ledger synthesis).
export const styleAPI = {
  getMine: async () => {
    const response = await api.get('/users/me/style');
    return response.data as {
      styleProfile: {
        archetype: {
          key: string;
          name: string;
          likelyStrength: string;
          likelyRisk: string;
          watchFor: string;
          watchBehaviours?: string[];
        };
        derived_from_quiz_version?: number;
        derived_at?: string;
      } | null;
      observations: {
        summary: string;
        watch_for: { attribute: string; status: string; evidence: string } | null;
        attributes: string[];
        source: 'openai' | 'deterministic';
        sessionsAnalyzed: number;
        computedAt: string;
      } | null;
    };
  },
};

// Preferences API
export const preferencesAPI = {
  getMine: async () => {
    const response = await api.get('/users/me/preferences');
    return response.data;
  },
  updateMine: async (patch: any) => {
    const response = await api.post('/users/me/preferences', patch || {});
    return response.data;
  },
  resetMine: async (mode: 'recommended' | 'default' = 'recommended') => {
    const response = await api.post('/users/me/preferences/reset', { mode });
    return response.data;
  },
};

// Onboarding (Requirement 1)
export const onboardingAPI = {
  getMine: async () => {
    const response = await api.get('/users/me/onboarding');
    return response.data;
  },
  updateMine: async (payload: { profile?: any; onboarding?: any }) => {
    const response = await api.post('/users/me/onboarding', payload || {});
    return response.data;
  },
};

// Baseline Diagnostic (Req 2/3)
export const baselineAPI = {
  bootstrap: async () => {
    const response = await api.get('/baseline/bootstrap');
    return response.data;
  },
  start: async (scenario_type?: string) => {
    const response = await api.post('/baseline/start', { scenario_type });
    return response.data;
  },
  skip: async (payload?: { sessionId?: string }) => {
    const response = await api.post('/baseline/skip', payload || {});
    return response.data;
  },
  /** On-demand prebrief orientation: who the character is, what happened
   *  before this conversation, and what success looks like. Generated and
   *  stored server-side on first request. */
  getOrientation: async (sessionId: string) => {
    const response = await api.get(
      `/baseline/session/${encodeURIComponent(sessionId)}/orientation`,
    );
    return response.data as {
      orientation: { who: string; backstory: string; success: string; personaName?: string };
      cached: boolean;
    };
  },
  anamToken: async (sessionId: string) => {
    const response = await api.post('/baseline/anam-token', { sessionId });
    return response.data;
  },
  /** ElevenLabs ConvAI config for voice-only baseline. Returns { signedUrl?, agentId, systemPrompt, firstMessage }. */
  getConvaiConfig: async (sessionId: string) => {
    const response = await api.get(`/baseline/session/${sessionId}/convai-config`);
    return response.data as {
      signedUrl?: string;
      agentId: string;
      systemPrompt: string;
      firstMessage: string;
      /** Model lab: the superadmin's chosen voice-agent brain, else null. */
      modelLab?: { llm: string | null } | null;
    };
  },
  /** Text-only baseline chat (OpenAI). messages: [{ role: 'user'|'assistant', content }]. Returns { message }. */
  chat: async (
    sessionId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) => {
    const response = await api.post('/baseline/chat', { sessionId, messages });
    return response.data as { message: string };
  },
  /** Finish the baseline conversation. `modality` is how the learner practised
   *  (video/voice/text) and is stored on the session. Response:
   *  { message, session, snapshot, profile }. */
  complete: async (payload: {
    sessionId: string;
    transcript: any[];
    durationSeconds?: number;
    modality?: 'video' | 'voice' | 'text';
  }) => {
    const response = await api.post('/baseline/complete', payload, { timeout: 85000 }); // 85s — just under backend's 90s watchdog
    return response.data;
  },
  getSession: async (sessionId: string) => {
    const response = await api.get(`/baseline/session/${sessionId}`);
    return response.data;
  },
  latest: async () => {
    const response = await api.get('/baseline/latest');
    return response.data;
  },
  /** The fifteen framework-aligned focus areas plus the one Lanura recommends
   *  for this learner (null when there is no evidence to base a pick on — the
   *  picker then shows no "Recommended" card). */
  focusAreas: async () => {
    const response = await api.get('/baseline/focus-areas');
    return response.data as { areas: FocusArea[]; recommended: RecommendedFocus | null };
  },
  /** Save the learner's focus. For a focus-area id the backend fills
   *  label/tags/domain from its config and ignores client values; legacy ids
   *  keep the client label/tags. `source` records whether the learner took the
   *  recommendation or chose their own. */
  setFocus: async (payload: {
    primary_focus_id: string;
    source?: FocusSource;
    primary_focus_label?: string;
    primary_focus_tags?: string[];
  }) => {
    const response = await api.post('/baseline/focus', payload);
    return response.data;
  },
};

// Pathways API
export const pathwaysAPI = {
  getMap: async () => {
    const response = await api.get('/pathways/map');
    return response.data;
  },
  getSituation: async (id: string) => {
    const response = await api.get(`/pathways/situations/${id}`);
    return response.data;
  },
  startSituation: async (id: string) => {
    const response = await api.post(`/pathways/situations/${id}/start`);
    return response.data;
  },
  getRole: async (id: string) => {
    const response = await api.get(`/pathways/roles/${id}`);
    return response.data;
  },
  recompute: async () => {
    const response = await api.post('/pathways/recompute');
    return response.data;
  },
  setPrimaryRole: async (roleId: string | null) => {
    const response = await api.post('/pathways/set-primary-role', { roleId });
    return response.data;
  },
};

// Analytics API
export const analyticsAPI = {
  // Score exports (CSV) - manager scope for managers, company scope for
  // admin/owner. Returned as Blobs for a client-side file download.
  exportSessionsCsv: async (params?: { from?: string; to?: string }) => {
    const response = await api.get('/analytics/export/sessions.csv', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  exportMembersCsv: async () => {
    const response = await api.get('/analytics/export/members.csv', { responseType: 'blob' });
    return response.data as Blob;
  },
  getCohortTrends: async (teamId?: string) => {
    const response = await api.get('/analytics/cohort-trends', {
      params: teamId ? { teamId } : undefined,
    });
    return response.data as {
      weeks: Array<{
        weekStart: string;
        sessions: number;
        activeMembers: number;
        overall: number | null;
        families: Record<string, number | null>;
      }>;
      members: number;
      teamId: string | null;
      teamLabel: string | null;
      totalSessions: number;
    };
  },
  getMyAnalytics: async (period: string = 'weekly') => {
    return await cachedGet(`/analytics/my`, { period }, 12_000);
  },

  getTeamAnalytics: async (period: string = 'weekly') => {
    const response = await api.get(`/analytics/team?period=${period}`);
    return response.data;
  },

  getCompanyAnalytics: async () => {
    const response = await api.get(`/analytics/company`);
    return response.data;
  },

  // Pilot progress against the signed success measures (manager scope and
  // up); the CSV is the same rows for the client's own reporting.
  // `companyId` only means anything for Lanura's superAdmin, in the Internal
  // Console (backend utils/pilotScope). Anyone else gets their own company.
  getPilotProgress: async (companyId?: string) => {
    const response = await api.get('/analytics/pilot', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data as PilotProgressPayload;
  },
  exportPilotCsv: async (companyId?: string) => {
    const response = await api.get('/analytics/export/pilot.csv', {
      responseType: 'blob',
      params: companyId ? { companyId } : undefined,
    });
    return response.data as Blob;
  },

  /** Companies running a pilot, for the Internal Console (superAdmin only, TM15). */
  listPilots: async () => {
    const response = await api.get('/analytics/pilots');
    return response.data as {
      pilots: Array<{
        companyId: string;
        companyName: string;
        pilotName: string;
        startsAt: string | null;
        endsAt: string | null;
      }>;
    };
  },
  getManagerInsights: async () => {
    return await cachedGet('/analytics/manager-insights', {}, 60_000);
  },

  getProgress: async (weeks: number = 5) => {
    const response = await api.get(`/analytics/progress?weeks=${weeks}`);
    return response.data;
  },

  updateAnalytics: async (period: string = 'weekly') => {
    const response = await api.post('/analytics/update', { period });
    return response.data;
  },
};

/**
 * Practice Plans and Assignments.
 *
 * A plan is a reusable set of scenarios. An assignment is one deployment of
 * it, with a due date per scenario. Progress is measured by the same maths as
 * a pilot's, so the payload below is the pilot progress payload.
 */
export type PracticePlan = {
  planId: string;
  name: string;
  description: string;
  scenarios: Array<{ simulationId: string; title: string; order: number }>;
  archived?: boolean;
  /** Who made it. A manager may change their own plans, not the owner's. */
  createdBy?: string | null;
  createdAt: string;
};
export type PracticeAudience =
  | { kind: 'company'; companyId?: string | null }
  | { kind: 'team'; teamId: string }
  | { kind: 'users'; userIds: string[] }
  /** PP4. The job role the learner picked, scoped to a team when a manager
   *  made it. A filter rather than a list: changing job role moves you in
   *  or out of it. */
  | { kind: 'role'; role: string; teamId?: string | null };
export type PracticeAssignment = {
  /** PP5. The note the assigner wrote for the learners. */
  message?: string;
  assignmentId: string;
  planId: string;
  name: string;
  audience: PracticeAudience;
  items: Array<{ simulationId: string; title: string; dueAt: string; order: number }>;
  window: { startsAt: string; endsAt: string; reviewAt: string | null; timezone: string };
  targets?: Record<string, number>;
  cohortSize?: number | null;
  status?: string;
  createdAt: string;
};

export const practicePlansAPI = {
  /** Plans, assignments and the library to pick scenarios from. */
  list: async () => {
    const response = await api.get('/practice-plans');
    return response.data as {
      plans: PracticePlan[];
      assignments: PracticeAssignment[];
      scenarios: Array<{ simulationId: string; title: string }>;
    };
  },
  /**
   * What the signed-in learner has been asked to do, by when, and what they
   * have finished. Same shape and same maths as `forLearner`, which is what
   * their manager reads, so the two cannot disagree about whether a scenario
   * is late.
   */
  mine: async () => {
    const response = await api.get('/practice-plans/my');
    return response.data as {
      assignments: Array<{
        assignmentId: string;
        planId: string;
        name: string;
        /** The note from whoever assigned it. Empty when none was written. */
        message: string;
        items: Array<{
          simulationId: string;
          title: string;
          dueAt: string;
          status: 'done' | 'late' | 'not_done';
          completedAt: string | null;
          attemptNumber: number | null;
        }>;
        startsAt: string | null;
        endsAt: string | null;
        completedAll: boolean;
        completedAllOnTime: boolean;
        additionalMinutes: number;
      }>;
    };
  },
  createPlan: async (payload: {
    name: string;
    description?: string;
    scenarios: Array<{ simulationId: string; title?: string }>;
  }) => {
    const response = await api.post('/practice-plans', payload);
    return response.data as { plan: PracticePlan };
  },
  updatePlan: async (
    planId: string,
    payload: {
      name?: string;
      description?: string;
      scenarios?: Array<{ simulationId: string; title?: string }>;
    },
  ) => {
    const response = await api.put(`/practice-plans/${planId}`, payload);
    return response.data as { plan: PracticePlan };
  },
  archivePlan: async (planId: string, archived = true) => {
    const response = await api.post(`/practice-plans/${planId}/archive`, { archived });
    return response.data as { plan: PracticePlan };
  },
  assign: async (
    planId: string,
    payload: {
      name?: string;
      /** PP5. One short note shown to the learner with the work. */
      message?: string;
      audience: PracticeAudience;
      startsAt: string;
      endsAt: string;
      reviewAt?: string | null;
      timezone?: string;
      dueAt?: string;
      items?: Array<{ simulationId: string; dueAt: string }>;
      cohortSize?: number | null;
    },
  ) => {
    const response = await api.post(`/practice-plans/${planId}/assignments`, payload);
    return response.data as { assignment: PracticeAssignment };
  },
  /** Change an assignment's name, note, audience or dates. Keeps what anyone has done. */
  updateAssignment: async (
    assignmentId: string,
    payload: {
      name?: string;
      message?: string;
      audience?: PracticeAudience;
      startsAt?: string;
      endsAt?: string;
      reviewAt?: string | null;
      items?: Array<{ simulationId: string; dueAt: string }>;
    },
  ) => {
    const response = await api.put(`/practice-plans/assignments/${assignmentId}`, payload);
    return response.data as { assignment: PracticeAssignment };
  },
  cancelAssignment: async (assignmentId: string) => {
    const response = await api.delete(`/practice-plans/assignments/${assignmentId}`);
    return response.data as { assignment: PracticeAssignment | null };
  },
  /**
   * What one person has been asked to do, and how far through it they are.
   * The other direction from `progress`, which is keyed by the assignment.
   */
  /** One line per assignment for the Assignments tab (MPR7 to MPR9). */
  assignmentSummary: async () => {
    const response = await api.get('/practice-plans/assignment-summary');
    return response.data as {
      summary: Record<
        string,
        {
          people: number;
          finished: number;
          started: number;
          notStarted: number;
          itemsDone: number;
          itemsTotal: number;
          overdue: number;
          overduePeople: number;
        }
      >;
    };
  },
  /** Assigned practice progress per person, for the People directory (TM9). */
  rosterProgress: async () => {
    const response = await api.get('/practice-plans/roster-progress');
    return response.data as {
      progress: Record<string, { done: number; total: number; overdue: number }>;
    };
  },
  forLearner: async (userId: string) => {
    const response = await api.get(`/practice-plans/learners/${userId}`);
    return response.data as {
      learner?: { userId: string; name: string; email: string };
      assignments: Array<{
        assignmentId: string;
        planId: string;
        name: string;
        startsAt: string | null;
        endsAt: string | null;
        items: Array<{
          simulationId: string;
          title: string;
          dueAt: string;
          status: 'done' | 'late' | 'not_done';
          completedAt: string | null;
          attemptNumber: number | null;
        }>;
        completedAll: boolean;
        completedAllOnTime: boolean;
        additionalMinutes: number;
      }>;
    };
  },
  /** One assignment's progress, plus the denominator it was measured against. */
  progress: async (assignmentId: string) => {
    const response = await api.get(`/practice-plans/assignments/${assignmentId}/progress`);
    return response.data as {
      assignment: {
        assignmentId: string;
        planId: string;
        planName: string;
        name: string;
        audience: PracticeAudience;
      };
      measuredAgainst: {
        cohortSize: number;
        declaredCohortSize: number | null;
        audienceSize: number;
        /** 'my-teams' when the assignment is wider than the reader and this
         *  is their slice of it. Absent on older responses. */
        scope?: 'all' | 'my-teams';
      };
      /**
       * A4. Whether the assignment moved anything: the four weeks before it
       * opened against the four after it closed. Carries a reason instead of
       * a figure when it is too soon, too few, or undated.
       */
      effect?:
        | { state: 'no_window' }
        | { state: 'too_soon'; readyAt: string; weeks: number }
        | { state: 'too_few'; learners: number; minLearners: number; weeks: number }
        | {
            state: 'measured';
            weeks: number;
            learners: number;
            before: number;
            after: number;
            delta: number;
            improved: number;
          };
      progress: PilotProgressPayload;
    };
  },
};

// Pilot API - what a learner (any role) may know about their company's pilot:
// which scenarios are assigned, so the session pages can say which one a
// session was. Config lives on companyAPI, progress on analyticsAPI.
export const pilotAPI = {
  getAssignments: async () => {
    const response = await api.get('/pilot/assignments');
    return response.data as { assignments: PilotAssignment[] };
  },
};

// Leaderboard API (company teammates)
export const leaderboardAPI = {
  getCompanyLeaderboard: async (period?: string) => {
    const response = await api.get('/leaderboard/company', {
      params: period ? { period } : undefined,
    });
    return response.data as {
      demo?: boolean;
      companyId: string | null;
      me?: string;
      items: Array<{
        id: string;
        name: string;
        email: string;
        minutesPracticed: number;
        bestStreak: number;
        achievementsCount: number;
        lastActive?: string | null;
      }>;
    };
  },
};

// Company API (admin invite links + invite lookup)
export const companyAPI = {
  getCompany: async (companyId: string) => {
    const response = await api.get(`/company/${encodeURIComponent(companyId)}`);
    return response.data as { company: { companyId: string; name: string } };
  },
  getMyInviteLink: async () => {
    // 5-min cache — the link only changes when a company is created;
    // createMyCompany busts the prefix.
    return (await cachedGet('/company/my/invite-link', {}, 5 * 60_000)) as {
      companyId: string | null;
      inviteUrl: string | null;
    };
  },
  createMyCompany: async (name: string) => {
    const response = await api.post('/company/my/company', { name });
    invalidateCachedGet('/company/'); // invite link is cached 5 min
    return response.data as { company: { companyId: string; name: string } };
  },
  // Owner/admin pilot config: which scenarios the cohort must complete, by
  // when, and the targets progress is measured against. GET also returns the
  // company library so the picker can offer it.
  getPilot: async (companyId?: string) => {
    const response = await api.get('/company/my/pilot', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data as PilotConfigResponse;
  },
  putPilot: async (pilot: PilotConfig, companyId?: string) => {
    const response = await api.put('/company/my/pilot', pilot, {
      params: companyId ? { companyId } : undefined,
    });
    return response.data as { pilot: PilotConfig };
  },
  deletePilot: async () => {
    const response = await api.delete('/company/my/pilot');
    return response.data;
  },
  // Owner-only: single-use invite that registers the holder as an admin of
  // the owner's company.
  generateAdminInvite: async () => {
    const response = await api.post('/auth/invite/generate', { type: 'admin' });
    return response.data as {
      token: string;
      inviteLink: string;
      type: 'admin';
      companyName: string | null;
      expiresAt: string | null;
    };
  },
  // Public lookup so /register can show the bound company + pre-check the
  // admin box. Throws (404) when the token is invalid/used.
  lookupInvite: async (token: string) => {
    const response = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
    return response.data as {
      valid: boolean;
      type: 'admin' | 'trial' | 'generic' | 'owner_referral' | 'replacement' | 'member';
      companyName: string | null;
      trialDays?: number | null;
      expiresAt?: string | null;
      /** Email invite to a team: the address it was sent to, the team, the role. */
      email?: string | null;
      teamName?: string | null;
      role?: 'employee' | 'manager' | null;
      invitedByName?: string | null;
    };
  },
};

// Skills API
export const skillsAPI = {
  getMySkills: async () => {
    const response = await api.get('/skills/my');
    return response.data;
  },

  updateSkills: async (skills: any, performance: any) => {
    const response = await api.put('/skills/my', { skills, performance });
    return response.data;
  },

  getTrendingSkills: async () => {
    const response = await api.get('/skills/trending');
    return response.data;
  },

  getRecommendations: async () => {
    const response = await api.get('/skills/recommendations');
    return response.data;
  },
};

// History API
export const historyAPI = {
  getMyHistory: async (
    page = 1,
    limit = 10,
    _category?: string,
    filters?: Record<string, string | undefined>,
  ) => {
    const response = await api.get('/history/my', {
      params: { page, limit, ...filters },
    });
    return response.data;
  },

  /**
   * Fetch another user's session history, grouped by scenario, filtered to
   * what the viewer is authorised to see. Powers the /admin/analytics
   * Employees-tab drill-in.
   *
   * Manager viewers are scoped to their managed teams' members; admin/owner
   * sees all company members.
   *
   * v2 multi-run latest-primary + completion-tracking exception:
   *   - Each scenario the target practised is one group.
   *   - `visibleRuns` carries the runs the viewer can fully see
   *     (canReadFeedback=true on each).
   *   - For company-reshared scenarios (mandatory training): `totalRunCount`
   *     and `latestRunAt` include hidden runs too; `hiddenRunCount > 0`
   *     means the practitioner ran it but didn't authorise feedback access
   *     for some/all sessions. Surface as a completion-only signal.
   *   - For non-company-reshared scenarios: hidden runs are dropped entirely
   *     — counts/timestamps reflect visible runs only (privacy: don't leak
   *     practice volume for voluntary practice).
   */
  getHistoryByUser: async (userId: string) => {
    const response = await api.get(`/history/by-user/${encodeURIComponent(userId)}`);
    return response.data as {
      scenarioGroups: Array<{
        scenarioId: string;
        scenarioTitle: string;
        scenarioCategory: string | null;
        totalRunCount: number;
        hiddenRunCount: number;
        latestRunAt: string | null;
        isCompanyReshared: boolean;
        visibleRuns: Array<{
          sessionId: string;
          completedAt: string | null;
          scores: any;
          feedback: any;
          growthEdge: string | null;
          durationSeconds: number;
          modality: string | null;
        }>;
      }>;
      /**
       * I4. A strength, a development focus and a question to open a
       * one-to-one with, built on the server from the same canReadFeedback
       * gate as `visibleRuns`. Null when it could not be computed.
       */
      coachingSummary: {
        basis: { sharedSessions: number; assignedItems: number; capability: boolean };
        strength: string | null;
        focus: string | null;
        prompt: string;
        note: string;
      } | null;
      /**
       * I1 and I2. Where they stand per behavioural domain and which way the
       * overall score is going, from the same gated sessions. Null when
       * nothing was shared, so the panel renders nothing rather than a
       * heading over a blank. No readiness figure, deliberately: see
       * backend/utils/capabilitySnapshot.js.
       */
      capability: {
        basis: { sharedSessions: number };
        standing: Array<{
          key: string;
          label: string;
          score: number;
          band: string;
          tier: 'strong' | 'developing' | 'focus' | null;
        }>;
        progress: {
          state: 'none' | 'one_point' | 'too_few' | 'measured';
          points: Array<{ at: string | null; score: number }>;
          sessions?: number;
          before?: number;
          after?: number;
          delta?: number;
          agreement?: number;
          domains?: Array<{
            key: string;
            label: string;
            before: number;
            after: number;
            delta: number;
          }>;
        };
      } | null;
    };
  },

  /**
   * Fetch full session report.
   *
   * `ownerUserId` is optional — pass the practitioner's userId when reading a
   * session that doesn't belong to the current viewer (cross-user reads via
   * the Employees-tab drill-in). Backend gates the cross-user path via
   * canReadFeedback. Omit for the legacy self-read path.
   */
  getSessionDetails: async (sessionId: string, ownerUserId?: string) => {
    const url = ownerUserId
      ? `/history/session/${sessionId}?userId=${encodeURIComponent(ownerUserId)}`
      : `/history/session/${sessionId}`;
    const response = await api.get(url);
    return response.data;
  },

  deleteSession: async (sessionId: string) => {
    const response = await api.delete(`/history/session/${sessionId}`);
    return response.data;
  },

  replaySession: async (sessionId: string) => {
    const response = await api.post(`/history/session/${sessionId}/replay`);
    return response.data;
  },

  getAnalytics: async (period = '30d') => {
    const response = await api.get('/history/analytics', {
      params: { period },
    });
    return response.data;
  },

  getRecordingsSummary: async (period?: 'week' | 'month' | 'quarter' | 'all') => {
    return await cachedGet('/history/recordings-summary', period ? { period } : {}, 12_000, {
      swr: true,
    });
  },

  getPracticeTotals: async () => {
    return await cachedGet('/history/practice-totals', {}, 12_000);
  },

  getStreakSummary: async () => {
    return await cachedGet('/history/streak-summary', {}, 12_000);
  },

  getPerceptionGap: async (limit?: number) => {
    const response = await api.get('/history/perception-gap', { params: limit ? { limit } : {} });
    return response.data;
  },
};

// Session Analytics (v2)
/** Rubric administration — the admin editor's API. Owner/admin only; every
 *  endpoint is company-scoped server-side, so an id from another workspace
 *  simply does not resolve. */
export interface RubricCriterion {
  key: string;
  label: string;
  guidance?: string;
  signals: Record<string, number>;
  spine?: string[];
}
/** `label` is what learners see on their report; `name` is the optional
 *  admin-facing band name shown in the editor's Scoring table (falls back to
 *  the label when unset). `anchor` is the band's display score. */
export interface RubricBand {
  key: string;
  label: string;
  upTo: number;
  anchor?: number;
  description?: string;
  name?: string;
}
export interface RubricDoc {
  id: string;
  label: string;
  description?: string;
  status?: 'draft' | 'published';
  version?: number | string | null;
  publishedVersion?: number | null;
  criteria: RubricCriterion[];
  bands: RubricBand[];
  seededFrom?: string | null;
  updatedAt?: string;
}

export const rubricsAPI = {
  list: async () => {
    const r = await api.get('/rubrics');
    return r.data as {
      defaultRubricId: string | null;
      builtIn: Array<{
        id: string;
        label: string;
        description: string;
        criteriaCount: number;
        readOnly: true;
      }>;
      own: Array<{
        id: string;
        label: string;
        description: string;
        status: string;
        archived: boolean;
        version: number | null;
        publishedVersion: number | null;
        criteriaCount: number;
        updatedAt: string;
        readOnly: false;
      }>;
    };
  },

  /**
   * Read a framework document and propose a rubric DRAFT.
   *
   * Never publishes. `unmapped` lists the criteria the document asks for that
   * we cannot measure — kept in the draft, in the customer's own words, so the
   * admin decides about the gap instead of discovering it in a blank report.
   */
  importFramework: async (file: File) => {
    const r = await api.post('/rubrics/import', file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-framework-filename': encodeURIComponent(file.name),
      },
      transformRequest: [(d) => d],
    });
    return r.data as {
      rubric: { id: string; label: string; criteriaCount?: number };
      unmapped: Array<{
        label: string;
        behaviours: string[];
        rejectedSignals: string[];
        reason: string;
      }>;
      problems: string[];
      wordCount: number;
    };
  },

  /**
   * What a scenario of this type can actually evidence of this rubric.
   * Three states, because the two kinds of gap have different answers:
   * `covered` always produces a number; `if_demonstrated` scores only when the
   * learner happens to do it (fixable by recategorising the scenario);
   * `not_assessable` has no detector at all (not fixable that way).
   */
  coverage: async (rubricId: string, scenarioType: string) => {
    const r = await api.get(
      `/rubrics/${encodeURIComponent(rubricId)}/coverage?scenarioType=${encodeURIComponent(scenarioType)}`,
    );
    return r.data as {
      rubric: { id: string; label: string };
      scenarioType: string;
      baselineType: string;
      usingFallback: boolean;
      criteria: Array<{
        key: string;
        label: string;
        state: 'covered' | 'if_demonstrated' | 'not_assessable';
        baselined: string[];
        unbaselined: string[];
        unknown: string[];
      }>;
      summary: { covered: number; ifDemonstrated: number; notAssessable: number; total: number };
    };
  },

  /** The signal palette: what a criterion can actually be built from.
   *  `labels` are the readable names the editor shows instead of the keys
   *  (older backends omit it; the editor humanises the key itself then). */
  signals: async () => {
    const r = await api.get('/rubrics/signals');
    return r.data as {
      signals: string[];
      byFamily: Record<string, string[]>;
      descriptions: Record<string, string>;
      labels?: Record<string, string>;
    };
  },

  get: async (id: string) => {
    const r = await api.get(`/rubrics/${encodeURIComponent(id)}`);
    return r.data as {
      rubric: RubricDoc;
      readOnly: boolean;
      hasDraft?: boolean;
      publishedVersion?: number | null;
      versions: Array<{ version: number; publishedAt: string }>;
      routing?: any;
    };
  },

  create: async (body: { label?: string; description?: string; seededFrom?: string }) => {
    const r = await api.post('/rubrics', body);
    return r.data as { rubric: RubricDoc };
  },

  /** Saves even when invalid — an admin mid-edit must not lose their work.
   *  The problems come back alongside so the editor can show them. */
  saveDraft: async (id: string, body: Partial<RubricDoc>) => {
    const r = await api.put(`/rubrics/${encodeURIComponent(id)}/draft`, body);
    return r.data as { rubric: RubricDoc; problems: string[] };
  },

  /** `problems` block publishing; `warnings` are criteria that will read
   *  "Not assessed yet" on most runs because nothing baselines their signals. */
  validate: async (id: string, scenarioType?: string) => {
    const r = await api.post(`/rubrics/${encodeURIComponent(id)}/validate`, { scenarioType });
    return r.data as {
      problems: string[];
      warnings: Array<{ criterion: string; label: string; message: string; scenarioType: string }>;
      scenarioTypes: string[];
    };
  },

  publish: async (id: string) => {
    const r = await api.post(`/rubrics/${encodeURIComponent(id)}/publish`, {});
    return r.data as { rubric: RubricDoc; version: number };
  },

  discardDraft: async (id: string) => {
    const r = await api.delete(`/rubrics/${encodeURIComponent(id)}/draft`);
    return r.data as { message: string };
  },

  /** What a publish would change and touch — the review-before-publish checkpoint. */
  publishSummary: async (id: string) => {
    const r = await api.get(`/rubrics/${encodeURIComponent(id)}/publish-summary`);
    return r.data as {
      nextVersion: number;
      changes: {
        added: string[];
        removed: string[];
        renamed: number;
        reweighted: number;
        bandsChanged: boolean;
      } | null;
      isDefault: boolean;
      usedBy: { count: number; titles: string[] };
      problems: string[];
    };
  },

  /** Hide from pickers without breaking anything that points at it. */
  setArchived: async (id: string, archived: boolean) => {
    const r = await api.post(
      `/rubrics/${encodeURIComponent(id)}/${archived ? 'archive' : 'unarchive'}`,
      {},
    );
    return r.data as { archived: boolean };
  },

  /** Published history, newest first, each with a what-changed summary. */
  versions: async (id: string) => {
    const r = await api.get(`/rubrics/${encodeURIComponent(id)}/versions`);
    return r.data as {
      versions: Array<{
        version: number;
        label: string;
        publishedAt: string | null;
        publishedBy: string | null;
        criteriaCount: number;
        bandCount: number;
        changes: {
          added: string[];
          removed: string[];
          renamed: number;
          reweighted: number;
          bandsChanged: boolean;
          detail?: {
            renamed: Array<{ from: string; to: string }>;
            reweighted: Array<{
              label: string;
              signals: Array<{ name: string; from: number | null; to: number | null }>;
            }>;
            bands: string[];
          };
        } | null;
      }>;
    };
  },

  /** Bring an old version back AS THE DRAFT. 409 (requiresForce) when the
   *  current draft holds unsaved work; pass force after the user confirms. */
  restore: async (id: string, version: number, force = false) => {
    const r = await api.post(`/rubrics/${encodeURIComponent(id)}/restore`, { version, force });
    return r.data as { rubric: RubricDoc; restoredFrom: number; publishedVersion: number | null };
  },

  getRouting: async (id: string) => {
    const r = await api.get(`/rubrics/${encodeURIComponent(id)}/routing`);
    return r.data as { custom: any; merged: any };
  },

  /** Live on save — a resource link is a pointer to help, so fixing a broken
   *  URL repairs every report carrying it, including old ones. */
  saveRouting: async (id: string, criteria: any) => {
    const r = await api.put(`/rubrics/${encodeURIComponent(id)}/routing`, { criteria });
    return r.data as { routing: any };
  },

  setDefault: async (rubricId: string | null) => {
    const r = await api.put('/rubrics/default', { rubricId });
    return r.data as { defaultRubricId: string | null };
  },

  /** The dry run: how a report would read, before publishing rather than after. */
  preview: async (id: string, scorecard: any) => {
    const r = await api.post(`/rubrics/${encodeURIComponent(id)}/preview`, { scorecard });
    return r.data as {
      rubric: { id: string; label: string; version: number | string };
      criteria: Array<{
        key: string;
        label: string;
        guidance: string;
        score: number | null;
        bandLabel: string | null;
      }>;
      routed: Array<{
        criterion: string;
        label: string;
        score: number;
        bandLabel: string;
        headline: string;
        resources: Array<{
          title: string;
          kind: string;
          minutes: number | null;
          url: string;
          /** 'web' when found by the web search (utils/webResources). */
          source?: string;
          site?: string;
        }>;
      }>;
    };
  },
};

export const sessionsAPI = {
  // Voice reflection coach config (post-session, own sessions only).
  getReflectionVoiceConfig: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/reflection-voice-config`);
    return response.data as {
      agentId: string;
      signedUrl?: string;
      systemPrompt: string;
      firstMessage: string;
    };
  },
  getAnalytics: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/analytics`);
    return response.data;
  },

  runAnalysis: async (
    sessionId: string,
    payload?: { scenario?: string; force?: boolean; transcript?: any[] },
  ) => {
    const response = await api.post(`/sessions/${sessionId}/run-analysis`, payload || {});
    return response.data;
  },

  getTagEvents: async (sessionId: string, tag?: string) => {
    const response = await api.get(`/sessions/${sessionId}/tag-events`, {
      params: tag ? { tag } : {},
    });
    return response.data;
  },

  exportAnalytics: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/export-analytics`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /** Share one of the learner's own past sessions with upper roles. One-way:
   *  it only turns sharing on. 403 `scenario_not_shared` when the scenario has
   *  no audience, 409 `not_completed` for an unfinished session. */
  shareSession: async (
    sessionId: string,
    opts: { transcript?: boolean } = {},
  ): Promise<{ feedbackVisibility: boolean; transcriptVisibility: boolean; changed: boolean }> => {
    const response = await api.post(`/sessions/${sessionId}/share`, {
      transcript: !!opts.transcript,
    });
    return response.data;
  },

  saveSelfRatings: async (
    sessionId: string,
    ratings: Record<string, number>,
    thoughts?: string,
    visibility?: {
      feedbackVisibility?: boolean;
      transcriptVisibility?: boolean;
      reflectionText?: string;
      /** Answers to the scenario's own questions (five-point scale). */
      reflection?: { answers: Array<{ subDimensionId: string; rating: string }> };
    },
  ) => {
    // reflectionText: the learner's own words from the reflection chat, so the
    // reflection-stage assessment reads the whole reflection, not only the
    // one-line "thoughts" the coach extracted.
    const response = await api.post(`/sessions/${sessionId}/self-ratings`, {
      ratings,
      thoughts,
      ...(visibility || {}),
    });
    return response.data;
  },

  /** The session's reflection questions: one per sub-dimension its scenario
   *  assesses, snapshotted on the session the first time they are asked. */
  getReflectionQuestions: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/reflection-questions`);
    return response.data as {
      frameworkVersion: string | null;
      source: 'ai' | 'manual' | 'default';
      questions: Array<{
        subDimensionId: string;
        domain: string | null;
        label: string;
        question: string;
        questionVersion: number;
      }>;
    };
  },

  getSelfRatings: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/self-ratings`);
    return response.data;
  },

  /** What to read and what to practise next, routed from this session's
   *  scores. Deterministic config lookup server-side — see
   *  backend/config/resourceRouting.json. Empty `routed` means nothing was
   *  scored yet, and the caller should render nothing rather than filler. */
  getNextSteps: async (sessionId: string) => {
    // The report page and its panel both ask for this on mount (four identical
    // calls in dev, where effects run twice). One request answers them all.
    const data = await cachedGet(`/sessions/${sessionId}/next-steps`);
    return data as {
      routed: Array<{
        criterion: string;
        label: string;
        score: number;
        band: string;
        bandLabel: string;
        headline: string;
        practiseCategory: string | null;
        /** The criterion's own key plus its spine families, so the report can
         *  file the writer's steps under it. Absent on older payloads. */
        families?: string[];
        resources: Array<{
          title: string;
          kind: string;
          minutes: number | null;
          url: string;
          /** 'web' when found by the web search (utils/webResources). */
          source?: string;
          site?: string;
        }>;
      }>;
      practise: null | {
        simulationId: string;
        title: string;
        category?: string;
        duration?: number;
        difficulty?: string;
        forCriterion: string;
        forCriterionLabel: string;
      };
      /** The scorecard in the customer's own vocabulary — every criterion the
       *  session was scored on, under the rubric it was FROZEN to. Null for
       *  sessions scored before rubrics existed. */
      rubric: null | {
        id: string;
        label: string;
        version: number;
        criteria: Array<{
          key: string;
          label: string;
          guidance: string;
          score: number | null;
          band: string | null;
          bandLabel: string | null;
          /** Why there is no score. 'not_observed' = the session never went
           *  there; 'insufficient_evidence' = what we saw was too thin to
           *  judge. A null score alone cannot tell those apart. */
          status: 'scored' | 'not_observed' | 'insufficient_evidence';
        }>;
      };
    };
  },

  /** Reflection coach: next thing the coach says. Stateless — the client
   *  holds the conversation and posts it back each turn. A 503 means the
   *  coach isn't configured; the caller falls back to the slider form. */
  reflectionChat: async (
    sessionId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) => {
    const response = await api.post(`/sessions/${sessionId}/reflection-chat`, { messages });
    return response.data as {
      message: string;
      done: boolean;
      answered: number;
      total: number;
      area: string | null;
    };
  },

  /** Turn the finished conversation into the same numbers the sliders would
   *  have produced. `ratings` comes back already expanded onto the six stored
   *  keys; `areas` is what the learner confirms. Saves nothing on its own. */
  reflectionExtract: async (
    sessionId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  ) => {
    const response = await api.post(`/sessions/${sessionId}/reflection-extract`, { messages });
    return response.data as {
      areas: Record<
        string,
        { label: string; value?: number; rating?: string; ratingLabel?: string }
      >;
      ratings: Record<string, number>;
      thoughts: string;
      /** Present when the coach asked the scenario's own questions. */
      tailored?: boolean;
      answers?: Array<{ subDimensionId: string; label: string; question: string; rating: string }>;
    };
  },

  /**
   * Adds the seconds a learner spent reading their report. Fired as the page
   * goes away, so it is a keepalive fetch rather than api.post for the same
   * reason saveSessionTranscript is: an XHR in flight when the document
   * unloads is cancelled, and sendBeacon cannot carry the auth headers.
   */
  postReviewTime: (sessionId: string, seconds: number) => {
    const whole = Math.floor(Number(seconds) || 0);
    if (!sessionId || whole <= 0) return undefined;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    Object.assign(headers, getCsrfHeader());
    try {
      return fetch(`${API_BASE_URL}/sessions/${sessionId}/review-time`, {
        method: 'POST',
        keepalive: true,
        credentials: 'include',
        headers,
        body: JSON.stringify({ seconds: whole }),
      })
        .then((r) => (r.ok ? (r.json() as Promise<{ reviewSeconds: number }>) : undefined))
        .catch(() => undefined);
    } catch {
      return undefined;
    }
  },

  // Fetch the FULL /try demo report for a claimed demo session. The backend
  // lazily generates and caches the enriched (all-skills-scored, unlocked)
  // report on first view; returns the lite teaser if it can't enrich.
  getDemoReport: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/demo-report`);
    return response.data as { report: any; cached: boolean };
  },
};

// Storage API (S3-backed, served via backend streaming endpoints)
// Tab-close protection for in-flight recording uploads (AF-4). The recording is
// uploaded right after the user finishes (post-navigation), so closing the tab in
// that brief window can cancel the request and lose the audio. While any recording
// upload is in flight we install a `beforeunload` guard so the browser warns before
// leaving — giving the upload time to complete. (Large binary bodies can't survive
// a real unload via sendBeacon/keepalive, which cap at ~64KB, so warn-and-wait is
// the robust mitigation short of chunked/resumable upload.)
let recordingUploadsInFlight = 0;
const recordingUnloadGuard = (e: BeforeUnloadEvent) => {
  if (recordingUploadsInFlight > 0) {
    e.preventDefault();
    e.returnValue = '';
    return '';
  }
};
const beginRecordingUpload = () => {
  if (recordingUploadsInFlight === 0 && typeof window !== 'undefined') {
    window.addEventListener('beforeunload', recordingUnloadGuard);
  }
  recordingUploadsInFlight += 1;
};
const endRecordingUpload = () => {
  recordingUploadsInFlight = Math.max(0, recordingUploadsInFlight - 1);
  if (recordingUploadsInFlight === 0 && typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', recordingUnloadGuard);
  }
};

export const storageAPI = {
  uploadSessionRecording: async (sessionId: string, blob: Blob) => {
    const contentType = (blob as any)?.type || 'application/octet-stream';
    beginRecordingUpload();
    try {
      const response = await api.put(`/storage/sessions/${sessionId}/recording`, blob, {
        headers: { 'Content-Type': contentType },
      });
      return response.data;
    } finally {
      endRecordingUpload();
    }
  },

  downloadSessionRecording: async (sessionId: string) => {
    const response = await api.get(`/storage/sessions/${sessionId}/recording`, {
      responseType: 'blob',
    });
    return response.data;
  },

  deleteSessionRecording: async (sessionId: string) => {
    const response = await api.delete(`/storage/sessions/${sessionId}/recording`);
    return response.data;
  },

  uploadTagEventSnippet: async (
    sessionId: string,
    tagEventId: string,
    blob: Blob,
    durationMs?: number,
  ) => {
    const contentType = (blob as any)?.type || 'application/octet-stream';
    const response = await api.put(
      `/storage/sessions/${sessionId}/tag-events/${tagEventId}/audio`,
      blob,
      {
        headers: { 'Content-Type': contentType },
        params: durationMs != null ? { durationMs } : undefined,
      },
    );
    return response.data;
  },

  downloadTagEventSnippet: async (sessionId: string, tagEventId: string) => {
    const response = await api.get(
      `/storage/sessions/${sessionId}/tag-events/${tagEventId}/audio`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  uploadProfileAvatar: async (blob: Blob) => {
    const contentType = blob.type || 'image/jpeg';
    await api.put('/storage/profile/avatar', blob, {
      headers: { 'Content-Type': contentType },
    });
  },

  getProfileAvatarBlob: async (): Promise<Blob | null> => {
    try {
      const response = await api.get('/storage/profile/avatar', { responseType: 'blob' });
      return response.data as Blob;
    } catch {
      return null;
    }
  },

  deleteProfileAvatar: async () => {
    await api.delete('/storage/profile/avatar');
  },
};

// superAdmin demo-preset builder (pinned identity only; server re-gates).
export type DemoPresetSummary = {
  slug: string;
  status: 'draft' | 'live';
  prospectName: string;
  title: string;
  personaName: string;
  skillCount: number;
  sourceFilename: string | null;
  createdAt: string;
  updatedAt: string;
};
export type DemoPreset = {
  slug: string;
  prospectName: string;
  styleLabel: string;
  challengeLabel: string;
  /* How hard the counterpart is to move. The same three levels the product's
     own scenarios use, so Hard means the same thing in a demo and in a real
     scenario. Optional: presets authored before this field have none, and keep
     exactly the behaviour they were written with. */
  difficulty?: 'easy' | 'medium' | 'hard' | null;
  maxSeconds: number;
  maxTurns: number;
  wrapAtTurns: number;
  anamMaxSessionLengthSeconds: number;
  persona: { name: string; gender: 'male' | 'female' };
  brief: {
    title: string;
    situation: string;
    yourGoal: string;
    counterpart: string;
    counterpartRole: string;
    userRole: string;
    counterpartStance: string;
    whatMakesDifficult: string;
    whatYouKnow: string;
    whatToUncover: string;
  };
  openingMessage: string;
  styleBlock: string;
  behaviourRules: string;
  fallback: { summary: string; missed: string[]; whyItMatters: string[]; strongerResponse: string };
  skills: Array<{ label: string; good: string; poor: string }>;
};
/**
 * The referral calculation method (superAdmin only).
 *
 * Rates and the expression itself live in the database, so a change here takes
 * effect on the next credit with no deploy on either side.
 */
/* One payload shape for preview and publish, so the thing that was previewed
   cannot differ in type from the thing that gets published. */
type ReferralMethodPayload = {
  method?: 'formula' | 'percent';
  expression?: string;
  rates?: Record<string, number>;
  percent?: number;
  minCredits?: number | null;
  maxCredits?: number | null;
  label?: string;
};

/* One partner commission, as the review list shows it. */
export type PartnerCommission = {
  pk: string;
  sk: string;
  status: 'pending' | 'approved' | 'rejected';
  partner: string;
  partnerId: string;
  company: string | null;
  customerEmail: string | null;
  paidAt: string;
  amountPaid: number;
  currency: string;
  billingMonths: number;
  contractMonths: number;
  proposedContractValue: number | null;
  contractValue: number;
  percent: number | null;
  commission: number;
  reviewedAt: string | null;
};

export const superAdminReferralAPI = {
  getFormula: async () => {
    const response = await api.get('/super-admin/referral/formula');
    return response.data as {
      formula: {
        version: number;
        /* Which of the two pricing methods is in force. A row saved before the
           minimal method existed has none, and the server resolves that to
           'formula'. */
        method?: 'formula' | 'percent';
        expression: string;
        rates: Record<string, number>;
        /* Minimal method. The two limits are null when unset, which is NOT the
           same as zero: a zero ceiling would pay nobody anything. */
        percent?: number;
        minCredits?: number | null;
        maxCredits?: number | null;
        label: string | null;
        updatedAt: string | null;
      };
      variables: Record<string, string>;
      functions: string[];
      defaults: { expression: string; rates: Record<string, number>; percent?: number };
      methods?: { FORMULA: string; PERCENT: string };
      percentBounds?: { min: number; max: number };
      samples: Array<{
        label: string;
        credits: number | null;
        amountPaid?: number;
        error?: string;
      }>;
    };
  },
  previewFormula: async (body: ReferralMethodPayload) => {
    const response = await api.post('/super-admin/referral/formula/preview', body);
    return response.data as {
      ok: boolean;
      error?: string;
      samples: Array<{ label: string; credits: number | null; error?: string }>;
    };
  },
  /* The event model, which is what actually pays product referrals. */
  getEvents: async () => {
    const response = await api.get('/super-admin/referral/events');
    return response.data as {
      config: {
        version: number;
        events: Record<string, number>;
        monthlyCapPerUser: number;
        expiryMonths: number;
        updatedAt: string | null;
      };
      meta: Record<string, { label: string; earns: string; hint: string }>;
      defaults: {
        events: Record<string, number>;
        monthlyCapPerUser: number;
        expiryMonths: number;
      };
    };
  },
  publishEvents: async (body: {
    events: Record<string, number>;
    monthlyCapPerUser: number;
    expiryMonths: number;
  }) => {
    const response = await api.put('/super-admin/referral/events', body);
    return response.data as { config: { version: number }; appliesTo: string };
  },

  /* Partners and their own commission rate (null = the global rate). */
  getPartners: async () => {
    const response = await api.get('/super-admin/referral/partners');
    return response.data as {
      data: Array<{
        accountId: string;
        name: string | null;
        email: string | null;
        commissionPercent: number | null;
        createdAt: string | null;
      }>;
      method: 'formula' | 'percent' | null;
      globalPercent: number;
      percentBounds: { min: number; max: number };
    };
  },
  setPartnerRate: async (accountId: string, commissionPercent: number | null) => {
    const response = await api.put(
      `/super-admin/referral/partners/${encodeURIComponent(accountId)}/rate`,
      { commissionPercent },
    );
    return response.data as { accountId: string; commissionPercent: number | null };
  },

  /* Partner commissions by review state (Jenny reviews every deal). */
  getCommissions: async (status: 'pending' | 'approved' | 'rejected' = 'pending') => {
    const response = await api.get('/super-admin/referral/commissions', { params: { status } });
    return response.data as { status: string; data: PartnerCommission[] };
  },
  reviewCommission: async (body: {
    pk: string;
    sk: string;
    decision: 'approve' | 'reject';
    contractValue?: number | null;
  }) => {
    const response = await api.post('/super-admin/referral/commissions/review', body);
    return response.data as { ok: boolean; status: string; approvedContractValue: number | null };
  },

  /* Competing partner referrals, flagged rather than acted on. */
  getDuplicates: async () => {
    const response = await api.get('/super-admin/referral/duplicates');
    return response.data as {
      data: Array<{
        leadKey: string;
        sk: string;
        company: string;
        alsoReferredBy: string | null;
        alsoReferredById: string | null;
        currentlyHeldBy: string | null;
        reason: 'held' | 'existing_customer' | 'expired';
        source: string | null;
        at: string;
      }>;
    };
  },
  resolveDuplicate: async (body: { leadKey: string; sk: string; reassignTo?: string | null }) => {
    const response = await api.post('/super-admin/referral/duplicates/resolve', body);
    return response.data as { ok: boolean; reassigned: boolean };
  },

  publishFormula: async (body: ReferralMethodPayload) => {
    const response = await api.put('/super-admin/referral/formula', body);
    return response.data as {
      formula: { version: number };
      appliesTo: string;
      samples: Array<{ label: string; credits: number | null }>;
    };
  },
};

export const superAdminDemosAPI = {
  parseBrief: async (slug: string, prospectName: string, file: File) => {
    const response = await api.post(
      `/super-admin/demo-presets/parse-brief?slug=${encodeURIComponent(slug)}&prospectName=${encodeURIComponent(prospectName)}`,
      file,
      // Below the server's 90s authoring watchdog so the server's own timeout
      // message wins on a slow call, not a generic client abort.
      { headers: { 'Content-Type': file.type || 'application/octet-stream' }, timeout: 85000 },
    );
    return response.data as { preset: DemoPreset; warnings: string[]; errors: string[] };
  },
  list: async () => {
    const response = await api.get('/super-admin/demo-presets');
    return response.data as { presets: DemoPresetSummary[] };
  },
  get: async (slug: string) => {
    const response = await api.get(`/super-admin/demo-presets/${slug}`);
    return response.data as {
      slug: string;
      status: 'draft' | 'live';
      preset: DemoPreset;
      sourceFilename: string | null;
    };
  },
  save: async (
    slug: string,
    preset: DemoPreset,
    status: 'draft' | 'live',
    sourceFilename?: string,
  ) => {
    const response = await api.put(`/super-admin/demo-presets/${slug}`, {
      preset,
      status,
      sourceFilename,
    });
    return response.data as { slug: string; status: string; errors: string[] };
  },
  setStatus: async (slug: string, status: 'draft' | 'live') => {
    const response = await api.post(`/super-admin/demo-presets/${slug}/status`, { status });
    return response.data as { slug: string; status: string };
  },
  remove: async (slug: string) => {
    const response = await api.delete(`/super-admin/demo-presets/${slug}`);
    return response.data;
  },
};

// Model lab (superAdmin-only): which models Lanura uses for THIS account's
// own sessions, with plain-English cost comparisons. The server gates every
// call on the pinned superAdmin identity and stores the choices on that one
// user record - nothing here can touch another account. Plain GETs (no
// cachedGet) so a save or reset is read back fresh.
export const modelLabAPI = {
  getCatalog: async () => {
    const response = await api.get('/model-lab/catalog');
    return response.data as ModelLabCatalog;
  },
  /** Partial: only the keys being changed; null on a key returns it to the default. */
  updateSettings: async (patch: Partial<ModelLabSelection>) => {
    const response = await api.put('/model-lab/settings', patch);
    return response.data as ModelLabCatalog;
  },
  /** Clears every override for this account. Returns the refreshed catalogue when the server sends one. */
  resetSettings: async () => {
    const response = await api.delete('/model-lab/settings');
    return (response.data || null) as ModelLabCatalog | null;
  },
  /** This account's last completed sessions that carry a model-lab stamp. */
  getSessions: async () => {
    const response = await api.get('/model-lab/sessions');
    const data = response.data;
    const rows = Array.isArray(data) ? data : data?.sessions || [];
    return rows as ModelLabSessionRow[];
  },
  /**
   * One short completion through the real chat path, for a purpose and
   * (when given) a specific catalogue model. The server answers 200 whether
   * the model replied or not: `{ ok: true, reply, ms, cost_usd, … }` or
   * `{ ok: false, error, status, … }` - the failure is the result.
   */
  testModel: async (body: { purpose: ModelLabTestPurpose; model?: string }) => {
    const response = await api.post('/model-lab/test', body);
    return response.data as ModelLabTestResult;
  },
};

// Company resource documents (coaching materials -> feedback routing).
// Uploads are two-step: create metadata, then PUT the raw file (house pattern:
// no multipart - raw body + filename header, matching storageAPI).
export const companyResourcesAPI = {
  getMeta: async () => {
    const response = await api.get('/company-resources/meta');
    return response.data as {
      subDimensions?: ResourceSubDimension[];
      families: Array<{ key: string; label: string }>;
    };
  },
  list: async () => {
    const response = await api.get('/company-resources');
    return response.data as { resources: CompanyResource[] };
  },
  create: async (payload: {
    title: string;
    description?: string;
    mappings?: ResourceMapping[];
    // Who may see it. Omitted means company-wide, which is what every
    // resource was before team playbooks existed.
    visibility?: ResourceVisibility;
    teamId?: string | null;
  }) => {
    const response = await api.post('/company-resources', payload);
    return response.data as { resource: CompanyResource };
  },
  uploadFile: async (resourceId: string, file: File) => {
    const response = await api.put(`/company-resources/${resourceId}/file`, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-Resource-Filename': encodeURIComponent(file.name || 'document'),
      },
    });
    return response.data as { resource: CompanyResource };
  },
  update: async (
    resourceId: string,
    payload: {
      title?: string;
      description?: string;
      mappings?: ResourceMapping[];
      visibility?: ResourceVisibility;
      teamId?: string | null;
    },
  ) => {
    const response = await api.patch(`/company-resources/${resourceId}`, payload);
    return response.data as { resource: CompanyResource };
  },
  remove: async (resourceId: string) => {
    const response = await api.delete(`/company-resources/${resourceId}`);
    return response.data;
  },
  getContext: async (resourceId: string) => {
    const response = await api.get(`/company-resources/${resourceId}/context`);
    return response.data as {
      sections?: Array<{ heading: string; snippet: string }>;
      wordCount?: number;
    };
  },
  downloadFile: async (resourceId: string) => {
    const response = await api.get(`/company-resources/${resourceId}/file`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

/** New mappings name one of the 15 behavioural sub-dimensions. Mappings saved
 *  before Oct 2026 name a legacy skill family instead and still recommend. */
export type ResourceMapping =
  | { subDimension: string; family?: undefined; maxScore: number }
  | { family: string; subDimension?: undefined; maxScore: number };
export type ResourceSubDimension = {
  key: string;
  label: string;
  domain: string;
  domainLabel: string;
};
/** 'company' is everyone in the workspace. 'team' is one team's own shelf. */
export type ResourceVisibility = 'company' | 'team';

/** An email invite to a team, as a manager sees it (QA-06). */
export interface TeamEmailInvite {
  token: string;
  email: string;
  role: 'employee' | 'manager';
  teamId: string;
  teamName: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invitedBy: string | null;
  createdAt: string | null;
  expiresAt: string | null;
  /** Only while it can still be used. */
  inviteLink: string | null;
}
export type CompanyResource = {
  resourceId: string;
  title: string;
  description: string;
  visibility: ResourceVisibility;
  teamId: string | null;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number;
  hasFile: boolean;
  parseStatus: 'no_file' | 'ready' | 'metadata_only';
  mappings: ResourceMapping[];
  createdAt: string;
  updatedAt: string;
  uploadedBy?: string;
};

// Users API
export const usersAPI = {
  getUsers: async (filters?: any) => {
    const response = await api.get('/users', { params: filters });
    return response.data;
  },

  getUser: async (id: string) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  updateUser: async (id: string, data: Partial<User>) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  getTeamMembers: async () => {
    const response = await api.get('/users/team/members');
    return response.data;
  },

  getTeamStats: async () => {
    const response = await api.get('/users/team/stats');
    return response.data;
  },

  /** @deprecated The account-level switch was retired on 20 Oct; sharing is
   *  decided per session on the reflection step. Nothing reads the field. */
  setManagerVisibility: async (shareWithManager: boolean) => {
    const response = await api.put('/users/me/visibility', { shareWithManager });
    return response.data as { shareWithManager: boolean };
  },
};

// Resources API
export const resourcesAPI = {
  list: async () => {
    const response = await api.get('/resources');
    return response.data;
  },
  create: async (payload: {
    name: string;
    type?: 'file' | 'url' | 'text';
    sizeBytes?: number;
    url?: string;
    text?: string;
  }) => {
    const response = await api.post('/resources', payload);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/resources/${id}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getData: async () => {
    return await cachedGet('/dashboard/data', {}, 10_000, { swr: true });
  },
};

// Testing API (dev-only backend guard)
// After a demo-chart toggle, the user's chartDemo override changed server-side
// but every dashboard/history surface caches aggressively (client cachedGet +
// react-query + a 12s backend cache). Bust the client layers and refetch so
// the journey/charts flip immediately instead of appearing "stuck".
async function bustDemoSurfaces() {
  invalidateCachedGet('/dashboard/data');
  invalidateCachedGet('/history/recordings-summary');
  invalidateCachedGet('/history/streak-summary');
  invalidateCachedGet('/history/practice-totals');
  invalidateCachedGet('/pathways/map');
  try {
    const { queryClient } = await import('../lib/queryClient');
    const { QK } = await import('../hooks/queries');
    queryClient.invalidateQueries({ queryKey: QK.dashboard });
    queryClient.invalidateQueries({ queryKey: QK.pathwaysMap });
    queryClient.invalidateQueries({ queryKey: ['history'] });
  } catch {
    /* react-query not mounted — cachedGet busts above still apply */
  }
}

export const testingAPI = {
  getChartDemo: async () => {
    const response = await api.get('/testing/charts');
    return response.data;
  },
  seedDemoCharts: async () => {
    const response = await api.post('/testing/charts/seed', {});
    await bustDemoSurfaces();
    return response.data;
  },
  resetDemoCharts: async () => {
    const response = await api.post('/testing/charts/reset', {});
    await bustDemoSurfaces();
    return response.data;
  },
  disableDemoCharts: async () => {
    const response = await api.post('/testing/charts/disable', {});
    await bustDemoSurfaces();
    return response.data;
  },
};

// Slack API
export const slackAPI = {
  connect: async () => {
    // Make authenticated request to get OAuth URL, then redirect
    const response = await api.get('/slack/connect');
    window.location.href = response.data.oauthUrl;
  },

  getStatus: async () => {
    const response = await api.get('/slack/status');
    return response.data;
  },

  getChannels: async () => {
    const response = await api.get('/slack/channels');
    return response.data;
  },

  selectChannel: async (channelId: string) => {
    const response = await api.post('/slack/select-channel', { channelId });
    return response.data;
  },

  disconnect: async () => {
    const response = await api.post('/slack/disconnect');
    return response.data;
  },

  sendTestMessage: async () => {
    const response = await api.post('/slack/test-message');
    return response.data;
  },

  getNudgeSettings: async () => {
    const response = await api.get('/slack/nudge-settings');
    return response.data;
  },

  updateNudgeSettings: async (settings: {
    nudgeEnabled?: boolean;
    nudgeFrequency?: 'daily' | 'weekdays';
    nudgeTime?: string;
  }) => {
    const response = await api.post('/slack/nudge-settings', settings);
    return response.data;
  },

  sendNudgeNow: async () => {
    const response = await api.post('/slack/nudge/send-now');
    return response.data;
  },
};

export const anamAPI = {
  getSimulationPersonas: async (signal?: AbortSignal) => {
    const response = await api.get('/anam/simulation-personas', { signal });
    return response.data;
  },

  getAvatarPreview: async (params?: Record<string, string>, signal?: AbortSignal) => {
    const response = await api.get('/anam/avatar-preview', { params, signal });
    return response.data;
  },

  getSessionToken: async (payload: {
    name?: string;
    mode?: string;
    personaKey?: string;
    simulationId?: string;
    systemPrompt?: string;
    contextGender?: 'male' | 'female';
  }) => {
    const response = await api.post('/anam/session-token', payload);
    return response.data;
  },
};

export const adminAPI = {
  /** List all company members (including self, flagged isSelf) with
   *  roles + permissions. 30-s cache — shared by Team access and the
   *  Seats board's bench; role / permission / ownership mutations
   *  below bust it. */
  getTeamMembers: async () => {
    return (await cachedGet('/users/admin/members', {}, 30_000, { swr: true })) as {
      members: Array<{
        id: string;
        name: string;
        email: string;
        role: import('../types').UserRole;
        permissions: import('../types').UserPermissions;
        lastActive: string | null;
        avatar: string;
        avatarUrl: string;
        /** Seat Model v2 — false = deactivated (can't log in, not in
         *  People Waiting; reactivate to restore seatless access). */
        isActive: boolean;
        createdAt: string | null;
        deactivatedAt: string | null;
        /** false when deactivated THIS billing cycle — reactivation is
         *  locked until the next renewal (revised 2026-06-11). */
        canReactivate: boolean;
      }>;
      /** Member-status summary (QA-05). `invited` is null until email
       *  invites exist. Optional so an older cached response still types. */
      summary?: {
        total: number;
        active: number;
        deactivated: number;
        byRole: { owner: number; admin: number; manager: number; employee: number };
        invited: number | null;
      };
    };
  },

  /** Deactivate a member (Seat Model v2 §6 — replaces deletion).
   *  Frees their seat (balance stays on it), blocks their login;
   *  history and stats preserved. Owner can never be deactivated. */
  deactivateMember: async (userId: string) => {
    const response = await api.post(`/users/admin/members/${userId}/deactivate`);
    invalidateCachedGet('/users/admin/members');
    invalidateCachedGet('/seats');
    return response.data as { ok: true; message: string; freedSeatNumber: number | null };
  },

  /**
   * Delete a member (soft). The record and their history stay, access ends,
   * and their seat is freed exactly as deactivation frees it. They leave
   * every roster and every team figure, because the two user-listing
   * functions filter deleted rows at source.
   *
   * The owner can never be deleted, and this route refuses self-deletion:
   * deleting your own account goes through accountDeletionAPI below, which
   * asks the owner first if you hold a seat.
   */
  deleteMember: async (userId: string, reason?: string) => {
    const response = await api.post(`/users/admin/members/${userId}/delete`, { reason });
    invalidateCachedGet('/users/admin/members');
    invalidateCachedGet('/seats');
    return response.data as { ok: true; freedSeatNumber: number | null; alreadyDone?: boolean };
  },

  /** Members who have asked to delete their account and hold a seat. */
  getDeletionRequests: async () => {
    const response = await api.get('/users/admin/deletion-requests');
    return response.data as {
      requests: Array<{
        userId: string;
        name: string | null;
        email: string | null;
        role: string | null;
        requestedAt: string;
        reason: string | null;
      }>;
    };
  },

  /** Owner only: the seat is theirs to release. */
  approveDeletionRequest: async (userId: string) => {
    const response = await api.post(`/users/admin/deletion-requests/${userId}/approve`);
    invalidateCachedGet('/users/admin/members');
    invalidateCachedGet('/seats');
    return response.data as { ok: true; freedSeatNumber: number | null };
  },

  /** Owner only. The member is notified and keeps their account. */
  declineDeletionRequest: async (userId: string) => {
    const response = await api.post(`/users/admin/deletion-requests/${userId}/decline`);
    invalidateCachedGet('/users/admin/members');
    return response.data as { ok: true };
  },

  /** The one read that asks for deleted rows. */
  getDeletedMembers: async () => {
    const response = await api.get('/users/admin/deleted-members');
    return response.data as {
      deleted: Array<{
        userId: string;
        name: string | null;
        email: string | null;
        role: string | null;
        deletedAt: string;
        deletedBy: string | null;
        deletedBySelf: boolean;
        reason: string | null;
      }>;
    };
  },

  /**
   * Owner only: undo a deletion. The account returns DEACTIVATED and
   * seatless, which is where a reactivated member starts too. Restoring
   * straight to active would hand back access, and possibly a seat, without
   * the owner choosing to.
   */
  restoreMember: async (userId: string) => {
    const response = await api.post(`/users/admin/members/${userId}/restore`);
    invalidateCachedGet('/users/admin/members');
    invalidateCachedGet('/seats');
    return response.data as { ok: true; state?: 'deactivated'; alreadyDone?: boolean };
  },

  /** Reactivate — returns the user to active SEATLESS state (no
   *  automatic seat re-grant). */
  reactivateMember: async (userId: string) => {
    const response = await api.post(`/users/admin/members/${userId}/reactivate`);
    invalidateCachedGet('/users/admin/members');
    invalidateCachedGet('/seats');
    return response.data as { ok: true; message: string };
  },
  /** Change a member's role (resets their permissions to role defaults). */
  updateMemberRole: async (userId: string, role: import('../types').UserRole) => {
    const response = await api.put(`/users/admin/members/${userId}/role`, { role });
    invalidateCachedGet('/users/admin/members');
    return response.data as {
      ok: boolean;
      role: string;
      permissions: import('../types').UserPermissions;
    };
  },
  /** Override specific permission flags for a member without changing their role. */
  updateMemberPermissions: async (
    userId: string,
    permissions: Partial<import('../types').UserPermissions>,
  ) => {
    const response = await api.put(`/users/admin/members/${userId}/permissions`, permissions);
    invalidateCachedGet('/users/admin/members');
    return response.data as { ok: boolean; permissions: import('../types').UserPermissions };
  },
  /** Transfer ownership to another company member (owner only). */
  transferOwnership: async (targetUserId: string) => {
    const response = await api.post('/users/owner/transfer-ownership', { targetUserId });
    invalidateCachedGet('/users/admin/members');
    invalidateCachedGet('/seats');
    return response.data as { ok: boolean; newOwnerId: string };
  },
  /** Dissolve the workspace. confirmName must match the company name exactly. */
  deleteWorkspace: async (confirmName: string) => {
    const response = await api.delete('/users/owner/workspace', { data: { confirmName } });
    return response.data as { ok: boolean };
  },
};

type TeamSummary = {
  teamId: string;
  name: string;
  managerIds: string[];
  managerId?: string;
  companyId: string;
  memberCount: number;
  archived?: boolean;
  assigned?: boolean;
};

export const teamsAPI = {
  list: async () => {
    const response = await api.get('/teams');
    return response.data as Array<TeamSummary>;
  },
  getDetail: async (teamId: string) => {
    const response = await api.get(`/teams/${teamId}`);
    return response.data as {
      teamId: string;
      name: string;
      managerIds: string[];
      archived?: boolean;
      assigned?: boolean;
      members: Array<{
        userId: string;
        name: string;
        email: string;
        /** Access role. */
        role: string;
        /** Job role, which PP4 groups an audience by. */
        position?: string;
      }>;
    };
  },
  /** The link a manager shares to fill their own team. Manager of it, or admin. */
  inviteLink: async (teamId: string) => {
    const response = await api.get(`/teams/${teamId}/invite-link`);
    return response.data as {
      teamId: string;
      teamName: string | null;
      companyId: string;
      inviteUrl: string;
    };
  },
  /** Email invites to this team (QA-06). One result per address. */
  sendEmailInvites: async (
    teamId: string,
    emails: string[] | string,
    role: 'employee' | 'manager' = 'employee',
  ) => {
    const response = await api.post(`/teams/${teamId}/invite-email`, { emails, role });
    return response.data as {
      teamId: string;
      role: 'employee' | 'manager';
      results: Array<{
        email: string;
        status:
          | 'sent'
          | 'link_only'
          | 'invalid_email'
          | 'already_member'
          | 'has_account'
          | 'already_invited';
        emailNotConfigured?: boolean;
        invite?: TeamEmailInvite;
      }>;
    };
  },
  /** This team's email invites, newest first. */
  listEmailInvites: async (teamId: string) => {
    const response = await api.get(`/teams/${teamId}/invites`);
    return response.data as { teamId: string; invites: TeamEmailInvite[] };
  },
  revokeEmailInvite: async (teamId: string, token: string) => {
    const response = await api.post(`/teams/${teamId}/invites/${encodeURIComponent(token)}/revoke`);
    return response.data as { invite: TeamEmailInvite };
  },
  /** Email it again with a fresh 7-day expiry. */
  resendEmailInvite: async (teamId: string, token: string) => {
    const response = await api.post(`/teams/${teamId}/invites/${encodeURIComponent(token)}/resend`);
    return response.data as {
      status: 'sent' | 'link_only';
      emailNotConfigured?: boolean;
      invite: TeamEmailInvite;
    };
  },
  /** Join a team from its invite link, as the signed-in person. Any role. */
  join: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/join`);
    return response.data as {
      success: true;
      teamId: string;
      teamName: string | null;
      already: boolean;
    };
  },
  // Manager → server ignores managerIds and auto-assigns self.
  // Admin/owner → must pass managerIds (>=1 manager).
  create: async (name: string, managerIds?: string[]) => {
    const response = await api.post('/teams', { name, managerIds });
    return response.data as { teamId: string; name: string };
  },
  rename: async (teamId: string, name: string) => {
    const response = await api.put(`/teams/${teamId}`, { name });
    return response.data;
  },
  // Disband (archive). Not a hard delete; owner can un-archive.
  disband: async (teamId: string) => {
    const response = await api.delete(`/teams/${teamId}`);
    return response.data as { success: boolean; archived: boolean };
  },
  unarchive: async (teamId: string) => {
    const response = await api.post(`/teams/${teamId}/unarchive`, {});
    return response.data as { success: boolean };
  },
  addMember: async (teamId: string, userId: string) => {
    const response = await api.post(`/teams/${teamId}/members`, { userId });
    return response.data as { success: boolean };
  },
  removeMember: async (teamId: string, userId: string) => {
    const response = await api.delete(`/teams/${teamId}/members/${userId}`);
    return response.data as { success: boolean };
  },
  addManager: async (teamId: string, userId: string) => {
    const response = await api.post(`/teams/${teamId}/managers`, { userId });
    return response.data as { success: boolean };
  },
  removeManager: async (teamId: string, userId: string) => {
    const response = await api.delete(`/teams/${teamId}/managers/${userId}`);
    return response.data as { success: boolean };
  },
};

export const reportsAPI = {
  /** Download a simulation feedback PDF. Triggers a browser file download.
   *  Returns a promise so callers can show loading state / handle errors —
   *  the existing SimulationReview "Export PDF" button still fire-and-forgets,
   *  the per-row Recordings export awaits + toasts on failure. */
  downloadSimulationPdf: async (sessionId: string) => {
    const token = getAccessToken() || '';
    const base = (api.defaults.baseURL || '/api').replace(/\/$/, '');
    const url = `${base}/reports/simulation/${encodeURIComponent(sessionId)}/pdf`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      // Carry the HTTP status so callers can explain the failure (404 = no
      // report exists for this session vs. 403 = not permitted).
      const err: any = new Error('Failed to download report');
      err.status = r.status;
      throw err;
    }
    const blob = await r.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `lanura-report-${sessionId}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
  },
};

/** Referrals dashboard (Settings → Referrals tab). Owner-only. */
export const referralsAPI = {
  /** Mint a new trackable trial invite. Body: { linkLabel? }.
   *  Returns the URL to share. */
  generateLink: async (opts?: { linkLabel?: string; trialDays?: number }) => {
    const response = await api.post('/referrals/generate-link', {
      linkLabel: opts?.linkLabel || undefined,
      trialDays: opts?.trialDays || undefined,
    });
    return response.data as {
      token: string;
      inviteLink: string;
      linkLabel: string | null;
      trialDays: number | null;
      expiresAt: string | null;
    };
  },
  /**
   * Mint a "Lanura Evaluation Access" invite: a trial carrying its OWN minute
   * allowance, for a prospect who has to evaluate the product rather than form
   * an impression.
   *
   * superAdmin ONLY, and the route lives under /auth rather than /referrals
   * because the backend proxies it to the email service, exactly as it does
   * for the owner-invite generator. It surfaces on the referrals tab, which is
   * why it is exported here.
   */
  generateEvaluationLink: async (opts: {
    label?: string;
    trialDays?: number;
    chatMinutes: number;
    voiceMinutes: number;
    videoMinutes: number;
  }) => {
    const response = await api.post('/auth/invite/generate-evaluation', opts);
    return response.data as {
      token: string | null;
      inviteLink: string;
      label: string | null;
      trialDays: number;
      trialLimits: { chatMinutes: number; voiceMinutes: number; videoMinutes: number };
      expiresAt: string | null;
    };
  },
  /** Current owner's minted links + per-link registered workspace info. */
  myLinks: async () => {
    const response = await api.get('/referrals/my-links');
    return response.data as {
      referrerUserId: string;
      totalLinks: number;
      /** What a standard link grants, straight from the backend constants. */
      standardTrial: {
        trialDays: number;
        chatMinutes: number;
        voiceMinutes: number;
        videoMinutes: number;
      };
      /** Upper bounds an evaluation link may not exceed. */
      evaluationCaps: {
        trialDays: number;
        chatMinutes: number;
        voiceMinutes: number;
        videoMinutes: number;
      };
      links: Array<{
        token: string;
        linkLabel: string | null;
        createdAt: string | null;
        expiresAt: string | null;
        used: boolean;
        // 'evaluation' links carry their own minute allowance (Lanura
        // Evaluation Access); 'standard' ones leave the workspace on the
        // default trial. Links minted before evaluation invites existed
        // report as 'standard'.
        kind: 'evaluation' | 'standard';
        trialDays: number | null;
        trialLimits: { chatMinutes: number; voiceMinutes: number; videoMinutes: number } | null;
        registrant: {
          userId: string;
          name: string | null;
          email: string | null;
          registeredAt: string | null;
          companyId: string | null;
          companyName: string | null;
          accountStatus: 'active' | 'trial';
          trialExpiresAt: string | null;
          subscription: {
            active: boolean;
            planName: string | null;
            planTier: string | null;
            renewsAt: string | null;
          };
        } | null;
      }>;
    };
  },
  /** Three counters for the strip above the table. */
  analytics: async () => {
    const response = await api.get('/referrals/analytics');
    return response.data as {
      linksGenerated: number;
      linksUsed: number;
      registrantsSubscribed: number;
    };
  },

  /** The owner's PERMANENT, multi-use referral link (lazily minted). */
  ownerLink: async () => {
    const response = await api.get('/referrals/owner-link');
    return response.data as {
      code: string;
      referralUrl: string;
      createdAt: string | null;
    };
  },

  /** The owner's own referral credit ledger, newest first. Same shape the
   *  referral portal returns, because it is the same ledger. */
  ownerCredits: async () => {
    const response = await api.get('/referrals/owner-credits');
    return response.data as {
      object: 'list';
      data: Array<{ id: string; credits: number; reason: string; at: string }>;
      has_more: boolean;
      creditsEarned: number;
    };
  },

  /** Everyone this owner referred (via the permanent link or any path),
   *  with workspace + subscription state, plus headline counts. */
  ownerRegistrants: async () => {
    const response = await api.get('/referrals/owner-registrants');
    return response.data as {
      totalRegistrants: number;
      subscribedCount: number;
      /* Owner referrals earn on the same credit system as the referral portal.
         Computed server-side from the stored formula, so a rate change shows
         here without a frontend deploy. */
      creditsEarned: number;
      registrants: Array<{
        userId: string;
        name: string | null;
        email: string | null;
        registeredAt: string | null;
        companyId: string | null;
        companyName: string | null;
        creditsEarned: number;
        breakdown: Record<string, unknown> | null;
        subscription: {
          active: boolean;
          planName: string | null;
          planTier: string | null;
        };
      }>;
    };
  },
};

/* ── Billing — Phase H surface ──────────────────────────────────────
 * Plan catalogue (GET /plans) + SuperAdmin Plan administration.
 * Phase C webhook + Phase E checkout / cancel land later. */
export type BillingPriceBlock = { price: string; subtitle: string };

export type BillingPublicPlan = {
  tier: 'core' | 'premium' | 'enterprise';
  name: string;
  tagline: string;
  annual: BillingPriceBlock;
  monthly: BillingPriceBlock;
  features: string[];
  ctaLabel: string;
  highlight: boolean;
  /** Raw per-seat amounts in pence: monthly = per-month, annual = per-year. */
  priceMonthlyPence?: number;
  priceAnnualPence?: number;
  contactSales?: boolean;
  contactEmail?: string | null;
};

export type BillingAdminPlan = BillingPublicPlan & {
  visible: boolean;
  syncStatus: 'unsynced' | 'live' | 'price-pending';
  stripeProductId: string | null;
  stripePriceIds: { monthly: string | null; annual: string | null };
  lastSyncedAt: string | null;
  /** Dual-mode sync state, one entry per Stripe environment (the legacy
   *  top-level fields mirror the instance's env-default mode). Optional so
   *  the panel keeps working against older backends. */
  stripeByMode?: Record<
    'live' | 'test',
    {
      mode: 'live' | 'test';
      stripeProductId: string | null;
      stripePriceIds: { monthly: string | null; annual: string | null };
      syncStatus: 'unsynced' | 'live' | 'price-pending';
      lastSyncedAt: string | null;
    }
  >;
  version: number;
  pricing: {
    monthly: { amount: number }; // pence
    annual: { amount: number }; // pence (yearly total)
  };
  updatedAt: string | null;
  updatedByEmail: string | null;
};

/** Called by every billing-mutating endpoint below. `/billing/plans`
 *  and `/billing/transactions` ARE now served via cachedGet (plans
 *  5 min, transactions 30 s), so this does real work. `/seats` rides
 *  along because billing mutations change the seat count (checkout
 *  quantity, change-seats, reconcile) and the board must not serve a
 *  stale Purchased figure after one. */
/** One Developer-API credit pack as the Top-up manager sees it. */
export type AdminCreditPack = {
  id: string;
  name: string;
  credits: number;
  price: number; // pence
  currency: string;
  active: boolean;
  sortOrder: number;
  version: number;
  updatedAt: string | null;
  updatedByEmail: string | null;
};

function invalidateBillingCaches() {
  invalidateCachedGet('/billing/');
  invalidateCachedGet('/auth/me');
  invalidateCachedGet('/seats');
}

export const billingAPI = {
  /** Plan catalogue. Any authenticated user. SuperAdmin gets the
   *  admin-shape payload with sync/Stripe-id fields exposed.
   *  5-min cache — the catalogue changes only via superAdmin edits,
   *  and patchPlan / syncPlan bust it on write. */
  getPlans: async () => {
    return (await cachedGet('/billing/plans', {}, 5 * 60_000, { swr: true })) as {
      plans: BillingPublicPlan[] | BillingAdminPlan[];
      isSuperAdmin: boolean;
    };
  },

  /** SuperAdmin patch — stages display copy / amount / visibility
   *  edits locally. Stripe push is the separate /sync step.
   *  Pass `version` from the loaded plan so If-Match catches a stale
   *  edit (409 on conflict). */
  patchPlan: async (
    tier: 'core' | 'premium' | 'enterprise',
    body: Partial<{
      displayName: string;
      tagline: string;
      features: string[];
      ctaLabel: string;
      highlight: boolean;
      visible: boolean;
      contactSales: boolean;
      contactEmail: string;
      pricing: { monthly?: { amount: number }; annual?: { amount: number } };
    }>,
    version: number,
  ) => {
    const response = await api.patch(`/billing/admin/plans/${tier}`, body, {
      headers: { 'If-Match': String(version) },
    });
    invalidateBillingCaches(); // plans are cached 5 min — bust on edit
    return response.data as {
      plan: BillingAdminPlan;
      changed?: string[];
      unchanged?: boolean;
    };
  },

  /* ── Top-up manager (superAdmin, Developer-API credit packs) ────
   * No Stripe sync step here: top-up checkouts use inline price_data,
   * so a saved edit is live for the very next purchase. */

  /** All credit packs, active and retired. SuperAdmin only. */
  getPacks: async () => {
    const response = await api.get('/billing/admin/packs');
    return response.data as { isSuperAdmin: boolean; packs: AdminCreditPack[] };
  },

  /** Edit one pack; `version` from the loaded row feeds If-Match. */
  patchPack: async (
    id: string,
    body: Partial<{
      name: string;
      credits: number;
      price: number;
      active: boolean;
      sortOrder: number;
    }>,
    version: number,
  ) => {
    const response = await api.patch(`/billing/admin/packs/${id}`, body, {
      headers: { 'If-Match': String(version) },
    });
    return response.data as { pack: AdminCreditPack; changed: string[] };
  },

  /** Add a new pack. */
  createPack: async (body: {
    id: string;
    name: string;
    credits: number;
    price: number;
    active?: boolean;
  }) => {
    const response = await api.post('/billing/admin/packs', body);
    return response.data as { pack: AdminCreditPack };
  },

  /** Every developer credit purchase, newest first (superAdmin). */
  getPackTopUps: async (limit = 50) => {
    const response = await api.get('/billing/admin/topups', { params: { limit } });
    return response.data as {
      total: number;
      topups: Array<{
        id: string;
        accountId: string;
        accountEmail: string | null;
        accountName: string | null;
        credits: number;
        amountMoneyMinor: number | null;
        currency: string;
        pack: string | null;
        stripeCheckoutSession: string | null;
        createdAt: string;
      }>;
    };
  },

  /** SuperAdmin push — mint / re-mint Stripe Product + Prices for
   *  the tier per the Bootstrap flow. */
  syncPlan: async (tier: 'core' | 'premium') => {
    const response = await api.post(`/billing/admin/plans/${tier}/sync`);
    invalidateBillingCaches(); // plans are cached 5 min — bust on sync
    return response.data as {
      plan: BillingAdminPlan;
      action: 'initial-sync' | 'price-change' | 'noop' | 'noop-status-clear';
      changes?: Record<string, { oldPriceId: string; newPriceId: string; newAmount: number }>;
    };
  },

  /** SuperAdmin — dual-mode Stripe: read the instance's active mode. */
  getStripeMode: async () => {
    const response = await api.get('/billing/admin/stripe-mode');
    return response.data as {
      mode: 'live' | 'test';
      defaultMode: 'live' | 'test';
      switchable: boolean;
      availableModes: Array<'live' | 'test'>;
    };
  },

  /** SuperAdmin — flip the active Stripe mode at runtime. Only works on
   *  instances started with STRIPE_MODE_SWITCHABLE=true (dev/local);
   *  everywhere else the endpoint 404s. */
  setStripeMode: async (mode: 'live' | 'test') => {
    const response = await api.post('/billing/admin/stripe-mode', { mode });
    invalidateBillingCaches(); // plan price ids are mode-scoped
    return response.data as { ok: boolean; mode: 'live' | 'test' };
  },

  /** Audit log per tier. Cursor pagination — pass the previous
   *  response's `lastEvaluatedKey` JSON-stringified as `before`. */
  getPlanAudit: async (
    tier: 'core' | 'premium' | 'enterprise',
    opts?: { limit?: number; before?: any },
  ) => {
    const params: Record<string, any> = {};
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.before) params.before = JSON.stringify(opts.before);
    const response = await api.get(`/billing/admin/plans/${tier}/audit`, { params });
    return response.data as {
      items: Array<{
        SK: string;
        tier: string;
        reason: string;
        before: any;
        after: any;
        actorEmail: string;
        createdAt: string;
      }>;
      lastEvaluatedKey: any;
    };
  },

  /** Owner-only — Phase E part 1. Mints a Stripe Checkout session for
   *  the chosen tier + cadence and returns the URL to redirect to.
   *  429 with reason: 'payment_attempt_locked' when a previous attempt
   *  is still in flight; 409 with reason: 'plan_not_synced' when
   *  superAdmin hasn't pushed Price IDs to Stripe yet. */
  createCheckoutSession: async (body: {
    planTier: 'core' | 'premium';
    cadence: 'monthly' | 'annual';
    /** Seat count (Stripe quantity). Defaults to 1 server-side. */
    seats?: number;
    /** 'signup' routes Stripe's cancel_url to the standalone /get-started/cancelled
     *  page (no app shell) instead of the in-app /subscription/cancelled. */
    flow?: 'signup';
  }) => {
    const response = await api.post('/billing/checkout-session', body);
    invalidateBillingCaches();
    return response.data as {
      url: string;
      sessionId: string;
      lock: {
        attemptId: string;
        lockedAt: string;
        lockExpiresAt: string;
        purpose: string;
      };
    };
  },

  /** PUBLIC signup checkout (Approach A): no account exists yet. Creates the
   *  Stripe Checkout session for the /get-started wizard; the account is
   *  provisioned only on payment success. */
  createSignupCheckoutSession: async (body: {
    planTier: 'core' | 'premium';
    cadence: 'monthly' | 'annual';
    seats?: number;
    name?: string;
    email?: string;
    password?: string;
    companyName: string;
    companyProfile?: any;
    authMethod: 'password' | 'google';
    googleCredential?: string;
    /** From a referral link's ?ref=. Stashed with the pending signup and
     *  resolved when the account is provisioned on payment success. */
    referralCode?: string;
  }) => {
    const response = await api.post('/billing/signup-checkout-session', body);
    return response.data as { url: string; sessionId: string };
  },

  /** Placeholder checkout for the self-serve /get-started wizard (stub mode
   *  only; 404 once real Stripe is on). Activates a paid Core/Premium plan with
   *  N seats and signs the owner's workspace into the product. Busts the
   *  /auth/me + billing caches so refreshUser() sees the active subscription. */
  stubActivate: async (body: {
    planTier: 'core' | 'premium';
    cadence: 'monthly' | 'annual';
    seats: number;
  }) => {
    const response = await api.post('/billing/stub-activate', body);
    invalidateBillingCaches();
    return response.data as {
      ok: boolean;
      alreadyActive?: boolean;
      subscription: {
        planTier: 'core' | 'premium';
        planName: string;
        cadence: 'monthly' | 'annual';
        seats: number;
        status: string;
        currentPeriodEnd: string;
      };
      seats: number;
    };
  },

  /** Owner-only — Stripe Customer Portal session. Fallback for payment-
   *  method management before Lanura's native UI ships. Requires the
   *  owner to have already subscribed (stripeCustomerId on record). */
  createPortalSession: async () => {
    const response = await api.post('/billing/portal-session');
    return response.data as { url: string };
  },

  /** Owner-only — period-end cancel. Stripe keeps the subscription
   *  active until the current period ends; the mirror flag flips via
   *  webhook. */
  cancel: async () => {
    const response = await api.post('/billing/cancel');
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    };
  },

  /** Owner-only — un-cancel a pending cancellation. */
  resume: async () => {
    const response = await api.post('/billing/resume');
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    };
  },

  /** Owner-only — release a pending downgrade / cadence-change schedule.
   *  Subscription continues unchanged at its current price. Counterpart
   *  to /change-plan and /switch-cadence when the change is scheduled
   *  rather than immediate. */
  cancelPendingChange: async () => {
    const response = await api.post('/billing/cancel-pending-change');
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      cancelled: true;
    };
  },

  /** Owner-only — prorated-cost preview for a seat-count change,
   *  WITHOUT applying it. Drives the Buy-More-Seats modal's
   *  "Prorated charge today" line. */
  previewSeats: async (body: { seats: number }) => {
    const response = await api.post('/billing/preview-seats', body);
    return response.data as {
      applied: 'immediate' | 'scheduled';
      seats: number;
      currentSeats: number;
      amountDue?: number;
      currency?: string;
      scheduledFor?: string | null;
    };
  },

  /** Owner-only — seat-count change on the current plan
   *  (Query/seatsSubscription.md). Increase = immediate + prorated;
   *  decrease = scheduled at renewal, 409 reason 'seats_occupied' when
   *  the target is below the occupied count. */
  changeSeats: async (body: { seats: number }) => {
    const response = await api.post('/billing/change-seats', body);
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      action: 'add-seats' | 'reduce-seats';
      applied: 'immediate' | 'scheduled';
      seats: number;
      previousSeats: number;
      scheduledFor?: string | null;
      currentPeriodEnd?: string | null;
      /** Reductions (v2 D1): seat numbers that will be auto-unseated
       *  at renewal, highest-first, never Seat #1. */
      willUnseat?: number[];
    };
  },

  /** Owner-only — cross-tier change. Backend forks per
   *  Query/ChangeInSubscription.md:
   *    - upgrade (newTier > currentTier) → applied immediately, prorated.
   *      Response carries `applied: 'immediate'` + currentPeriodEnd.
   *    - downgrade (newTier < currentTier) → scheduled for next renewal.
   *      Response carries `applied: 'scheduled'` + scheduledFor +
   *      scheduleId. Nothing charged today. */
  changePlan: async (body: { planTier: 'core' | 'premium'; cadence: 'monthly' | 'annual' }) => {
    const response = await api.post('/billing/change-plan', body);
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      action: 'upgrade' | 'downgrade';
      applied: 'immediate' | 'scheduled';
      planTier: 'core' | 'premium';
      cadence: 'monthly' | 'annual';
      currentPeriodEnd?: string | null;
      scheduledFor?: string | null;
      scheduleId?: string | null;
    };
  },

  /** Owner-only — same-tier cadence flip. ALWAYS scheduled per
   *  Query/ChangeInSubscription.md rule 4 (delayed regardless of which
   *  cadence costs more per unit). Nothing charged today. */
  switchCadence: async (body: { cadence: 'monthly' | 'annual' }) => {
    const response = await api.post('/billing/switch-cadence', body);
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      action: 'switch-cadence';
      applied: 'scheduled';
      planTier: 'core' | 'premium';
      cadence: 'monthly' | 'annual';
      scheduledFor: string | null;
      scheduleId: string | null;
    };
  },

  /** Owner-only — Phase E part 2. Returns the prorated invoice preview
   *  for a proposed change, WITHOUT applying it. Drives the comparison
   *  drawer's "You'll be charged £X today" copy. Negative amountDue
   *  is a credit applied on next renewal (downgrade case). */
  previewChange: async (body: { planTier: 'core' | 'premium'; cadence: 'monthly' | 'annual' }) => {
    const response = await api.post('/billing/preview-change', body);
    return response.data as {
      amountDue: number; // smallest currency unit (pence for GBP)
      subtotal: number; // pre-tax
      tax: number; // VAT or other
      currency: string; // 'gbp' (or local if Adaptive Pricing kicked in)
      nextPaymentAttempt: string | null;
      isCredit: boolean;
      // Itemised proration breakdown — each row maps directly to a
      // Stripe invoice line item. Drives the drawer's transparent
      // calculation view.
      lines: Array<{
        description: string;
        amount: number;
        currency: string;
        isCredit: boolean;
        isProration: boolean;
        period: { start: string; end: string } | null;
      }>;
      // What the user will be charged on the next renewal date, AT
      // THE NEW RATE. Separate from amountDue (which is just today).
      nextRenewal: {
        amount: number;
        currency: string;
        date: string | null;
      };
    };
  },

  /** Owner-only — compresses the paymentAttemptLock to a short
   *  cooldown (~2 min) once the owner returns from Stripe Checkout
   *  via the cancel path. Idempotent; never extends. CheckoutCancelled
   *  calls this on mount. */
  releaseLock: async () => {
    const response = await api.post('/billing/release-lock');
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      action: 'compressed' | 'already-expiring-soon' | 'no-lock';
      lockExpiresAt?: string;
    };
  },

  /** Owner-only — Phase E follow-up. Authoritative verification of a
   *  Stripe Checkout session. Backend retrieves the session from
   *  Stripe with sub + invoice expanded, asserts the customer matches
   *  the caller, then persists subscription mirror + transaction row
   *  if payment_status is 'paid'. 'pending' status means the success
   *  page should re-poll or render a "still processing" surface. */
  verifySession: async (sessionId: string) => {
    const response = await api.post('/billing/verify-session', { sessionId });
    invalidateBillingCaches();
    return response.data as {
      status: 'verified' | 'pending';
      payment_status?: string;
      sessionId: string;
      subscription?: {
        stripeSubscriptionId: string;
        planName: string | null;
        planTier: string | null;
        cadence: string | null;
      } | null;
      invoice?: {
        stripeInvoiceId: string;
        created: boolean;
        amount: number;
        currency: string;
      } | null;
    };
  },

  /** Owner-only — pulls the current subscription + invoices straight
   *  from Stripe and persists them onto the user record + transaction
   *  store. Same writes the Phase C webhook would do, just on-demand.
   *  Idempotent. Used by CheckoutReturn so dev environments without
   *  `stripe listen` still get a populated subscribed surface +
   *  Transactions tab immediately after a Subscribe. */
  reconcileSubscription: async () => {
    const response = await api.post('/billing/reconcile-subscription');
    invalidateBillingCaches();
    return response.data as {
      ok: true;
      mirrored: boolean;
      transactions: number;
      transactionsCreated: number;
      stripeSubscriptionId?: string;
      // Backend sets this when reconcile finds an active subscription
      // and clears any in-flight paymentAttemptLock. Surfaced so the
      // Subscription page lock-strip Sync button can toast accurately.
      lockCleared?: boolean;
    };
  },

  /** Owner-only — Phase G. Lists the caller's transaction history,
   *  newest first, cursor-paginated. Pass the previous response's
   *  `lastEvaluatedKey` as `before` (JSON-stringified) to page. */
  getTransactions: async (opts?: {
    limit?: number;
    before?: any;
    status?: 'paid' | 'failed' | 'refunded' | 'pending';
  }) => {
    const params: Record<string, any> = {};
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.before) params.before = JSON.stringify(opts.before);
    if (opts?.status) params.status = opts.status;
    // 30-s cache keyed by the full param set (cursor pages cache
    // independently). Every billing mutator + the reconcile path bust
    // the '/billing/' prefix, so money UI never shows post-mutation
    // stale rows — the TTL only bounds webhook-driven drift.
    return (await cachedGet('/billing/transactions', params, 30_000, { swr: true })) as {
      items: Array<{
        id: string;
        stripeInvoiceId: string;
        amount: number; // pence (≥ 0; mirrors invoice.amount_paid)
        // Signed pence (mirrors invoice.subtotal). Negative for
        // plan-change credits where Stripe parked the unused-time
        // refund on customer.balance instead of refunding the card.
        // The TransactionsTab summary card sums this — credits subtract,
        // charges add — so the "Paying this cycle" net is honest.
        subtotal: number;
        currency: string;
        status: 'paid' | 'failed' | 'refunded' | 'pending';
        description: string;
        stripeReceiptUrl: string | null;
        lanuraReceiptUrl: string | null;
        reason: string | null;
        createdAt: string;
      }>;
      lastEvaluatedKey: any;
    };
  },

  /** SuperAdmin-only — workspace-wide oversight. Lists every
   *  transaction across every owner, newest first, with the
   *  originating owner's name + email hydrated onto each row.
   *  Same cursor + status semantics as getTransactions. */
  getAllTransactions: async (opts?: {
    limit?: number;
    before?: any;
    status?: 'paid' | 'failed' | 'refunded' | 'pending';
  }) => {
    const params: Record<string, any> = {};
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.before) params.before = JSON.stringify(opts.before);
    if (opts?.status) params.status = opts.status;
    const response = await api.get('/billing/admin/transactions', { params });
    return response.data as {
      items: Array<{
        id: string;
        stripeInvoiceId: string;
        amount: number;
        currency: string;
        status: 'paid' | 'failed' | 'refunded' | 'pending';
        description: string;
        stripeReceiptUrl: string | null;
        lanuraReceiptUrl: string | null;
        reason: string | null;
        createdAt: string;
        owner: {
          userId: string;
          name: string | null;
          email: string | null;
          company: string | null;
        };
      }>;
      lastEvaluatedKey: any;
    };
  },
};

/* ── Seats API — Seat Model v2 (Query/SeatFlowV2-FINAL.md) ──────────
 * A seat = one license for one person. There is NO unassign: seats
 * change hands only via Replace User (max 1 per seat per cycle) and
 * free up only via deactivation or a scheduled reduction landing.
 * Seat-COUNT changes are billing operations (billingAPI.changeSeats). */

export interface SeatView {
  seatNumber: number;
  /** Seat #1 — the owner's pinned seat. Never replaceable, never
   *  unseated, no drag affordances. */
  isOwnerSeat: boolean;
  status: 'occupied' | 'vacant';
  assignedUserId: string | null;
  assignedAt: string | null;
  assignee: { userId: string; name: string | null; email: string | null } | null;
  usage: { chatSeconds: number; voiceSeconds: number; videoSeconds: number };
  /** null = unmetered. */
  remaining: {
    chatSeconds: number | null;
    voiceSeconds: number | null;
    videoSeconds: number | null;
  };
  /** Replace User is gated on credit usage (revised 2026-06-11): once
   *  any minute is practised on the seat, Replace locks and the holder
   *  can only be changed by Deactivate. Lock lifts at the next renewal. */
  creditsUsedThisCycle: boolean;
  replaceLockedUntil: string | null;
  /** Pending Path-B replacement invite, if any. */
  pendingReplacement: {
    email: string | null;
    token: string | null;
    outgoingUserId: string | null;
    createdAt: string | null;
  } | null;
}

export interface SeatBoard {
  subscribed: boolean;
  /** True when the workspace is exempt from seat enforcement (e.g.
   *  Lanura Demo Company / superAdmin). No seats need buying. */
  seatExempt?: boolean;
  /** Trial board (D2): exactly 1 owner seat carrying the one-time
   *  100 chat / 30 voice / 5 video minute allowance. */
  trial?: boolean;
  tier?: 'core' | 'premium' | 'enterprise';
  cycle?: { start: string; end: string };
  limits?: { chatSeconds: number | null; voiceSeconds: number | null; videoSeconds: number | null };
  counts: { purchased: number; occupied: number; vacant: number };
  seats: SeatView[];
}

export const seatsAPI = {
  /** Owner-only — the full seat board for Settings → Seats.
   *  12-s cache + SWR; seat mutations and every billing mutation bust
   *  the '/seats' prefix. */
  getBoard: async () => {
    return (await cachedGet('/seats', {}, 12_000, { swr: true })) as SeatBoard;
  },

  /** Any member — own seat state + (trial) the shared chat pool.
   *  Drives the practice meter and seatless surfaces. */
  getMine: async () => {
    return (await cachedGet('/seats/me', {}, 12_000, { swr: true })) as {
      subscribed: boolean;
      trial?: boolean;
      seated?: boolean;
      message?: string;
      tier?: 'core' | 'premium' | 'enterprise';
      cycle?: { start: string; end: string };
      seatNumber?: number;
      isOwnerSeat?: boolean;
      limits?: {
        chatSeconds: number | null;
        voiceSeconds: number | null;
        videoSeconds: number | null;
      };
      usage?: { chatSeconds: number; voiceSeconds: number; videoSeconds: number };
      remaining?: {
        chatSeconds: number | null;
        voiceSeconds: number | null;
        videoSeconds: number | null;
      };
      /** Trial owner's chat pool (only the owner is seated on trial). */
      trialChat?: {
        limitSeconds: number | null;
        usedSeconds: number;
        remainingSeconds: number | null;
      } | null;
    };
  },

  /** Owner-only — seat an ACTIVE seatless member onto a VACANT seat
   *  (§4.2). One-directional; the assignee inherits the seat's
   *  remaining credits. */
  assign: async (seatNumber: number, userId: string) => {
    const response = await api.post(`/seats/${seatNumber}/assign`, { userId });
    invalidateCachedGet('/seats');
    return response.data as { ok: true; seat: SeatView };
  },

  /** Owner-only — Replace User (§5). Pass { userId } for Path A
   *  (existing seatless member, transfers immediately) or { email }
   *  for Path B (targeted invite link; transfers when they register).
   *  409 reason 'credits_used' once the seat has been practised on
   *  this cycle (revised 2026-06-11 — deactivate to change the holder),
   *  'replacement_pending' when an invite is already out. */
  replace: async (seatNumber: number, body: { userId?: string; email?: string }) => {
    const response = await api.post(`/seats/${seatNumber}/replace`, body);
    invalidateCachedGet('/seats');
    return response.data as {
      ok: true;
      mode: 'transferred' | 'invited';
      seat: SeatView;
      outgoingUserId?: string;
      inviteLink?: string;
      expiresAt?: string | null;
    };
  },

  /** Owner-only — cancel a pending Path-B replacement invite. The
   *  per-cycle counter is NOT consumed (it only burns on transfer). */
  cancelReplaceInvite: async (seatNumber: number) => {
    const response = await api.post(`/seats/${seatNumber}/replace/cancel`);
    invalidateCachedGet('/seats');
    return response.data as { ok: true; cancelled: true };
  },
};

export default api;

/**
 * Deleting your OWN app account.
 *
 * Which of the two paths the button takes depends on whether you hold a paid
 * seat, because that seat belongs to whoever pays for it:
 *
 *   seatless  ->  deleted immediately
 *   seated    ->  a request the workspace owner approves
 *
 * Ask getDeletionState() first so the screen can say which, up front, rather
 * than after the fact. A workspace owner cannot delete their account at all
 * and has to transfer ownership first.
 */
export const accountDeletionAPI = {
  getState: async () => {
    const response = await api.get('/users/me/deletion-state');
    return response.data as {
      canDelete: boolean;
      blockedReason: 'owner_must_transfer_first' | null;
      needsOwnerApproval: boolean;
      pending: boolean;
      requestedAt: string | null;
    };
  },

  /** Deletes, or asks the owner to. The reply says which happened. */
  requestDeletion: async (reason?: string) => {
    const response = await api.post('/users/me/delete', { reason });
    return response.data as {
      ok: true;
      status: 'deleted' | 'pending_owner_approval';
      freedSeatNumber?: number | null;
      seatNumber?: number | null;
      ownerNotified?: boolean;
      alreadyDone?: boolean;
    };
  },

  /** Withdraw my own pending request. */
  cancelDeletion: async () => {
    const response = await api.delete('/users/me/delete');
    return response.data as { ok: true; status: 'active'; alreadyDone?: boolean };
  },
};
